import React, { useMemo, useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { FaExclamationTriangle } from 'react-icons/fa';
import { getBlockers } from '../../../utils/idHelpers';
import {
    getStatusColour,
    PARENT_COLOUR,
    CLIENT_PARENT_COLOUR,
    STATUS_COLOUR,
    getBarGradient,
    getNumericRef,
    getColumnMetrics,
    anchoredScaleScroll,
    dragTypeFor,
    snapUiScale,
    UI_SCALE_DEFAULT,
    TOUCH_TAP_SLOP,
    ganttTextVisible,
    fitUiScale,
    percentLabelFits,
    barNameOverflows,
    statusLabel,
    BLOCKED_ICON_WIDTH,
    AFTER_BAR_NAME_CLASSES,
    AFTER_BAR_NAME_GLOW,
    AFTER_BAR_NAME_OFFSET
} from './gantt/GanttUtils';
import { useGanttState } from './gantt/GanttState';
import { useAnchoredPlacement } from './gantt/useAnchoredPlacement';
import GanttDragConfirm from './gantt/GanttDragConfirm';
import { useGanttPan } from './gantt/GanttPan';
import { useGanttGestures } from './gantt/GanttGestures';
import { clampMove, cascadeResize } from '../../../utils/fsConstraints';
import ZoomSlider from './gantt/ZoomSlider';
import { makeTextMeasurer, flowWords } from './gantt/nameFlow';
import { applyViewMode, expansionFor } from './gantt/viewModes';
import DateSlider from './gantt/DateSlider';
import { StatusChangePopup, AssigneeChangePopup, DependencyColourPicker, CustomTooltip } from './gantt/GanttPopups';
import DependencyArrows from './gantt/DependencyArrows';
import { buildGanttRows } from './gantt/GanttRowBuilder';
import { formatRelativeDay, isTemplateWeekend, calculateTemplateDuration } from '../../../utils/templateDateUtils';
import { weekendWorkKey } from '../../../utils/templateWeekendWork';
import Tooltip from '../../../components/Tooltip';
import { effortLabel, isClientLaneTask } from '../../../utils/effortDisplay';

// Collapsible-column chevrons (organise T162 tidy-up style)
const COL_CHEVRON_CLASS = 'flex-shrink-0 text-lg leading-4 text-gray-700 hover:text-gray-900';

// Template mode labels its month columns W1-W4, so a month here is four weeks,
// not a calendar month. Every getColumnMetrics call in this file passes it.
const TEMPLATE_MONTH_DAYS = 28;

// Calculate bar segments for a subtask, splitting at non-worked weekends
const calculateTemplateBarSegments = (row, pixelsPerDay, minDay, weekendWorkData) => {
    if (row.start == null || row.end == null) return [];

    const startDay = row.start;
    const endDay = row.end;
    const subtaskWork = weekendWorkData[weekendWorkKey(row.projectId, row.originalId)] || {};
    const segments = [];
    let segmentStart = null;
    let segmentIsWeekend = false;

    for (let d = startDay; d <= endDay; d++) {
        const dayIsWeekend = isTemplateWeekend(d);
        const isWorked = dayIsWeekend ? (subtaskWork[String(d)] === true) : true;

        if (segmentStart === null) {
            segmentStart = d;
            segmentIsWeekend = dayIsWeekend && !isWorked;
        } else {
            const currentShouldBeWeekendGap = dayIsWeekend && !isWorked;
            if (currentShouldBeWeekendGap !== segmentIsWeekend) {
                const segmentDays = d - segmentStart;
                const startOffset = segmentStart - minDay;
                segments.push({
                    left: startOffset * pixelsPerDay,
                    width: segmentDays * pixelsPerDay,
                    isWeekendGap: segmentIsWeekend
                });
                segmentStart = d;
                segmentIsWeekend = currentShouldBeWeekendGap;
            }
        }
    }

    // Add final segment
    if (segmentStart !== null) {
        const segmentDays = Math.max(endDay - segmentStart + 1, 1);
        if (segmentDays > 0) {
            const startOffset = segmentStart - minDay;
            segments.push({
                left: startOffset * pixelsPerDay,
                width: segmentDays * pixelsPerDay,
                isWeekendGap: segmentIsWeekend
            });
        }
    }

    return segments;
};

// Get which relative day was clicked from pixel position within a bar
const getClickedDay = (clickX, barLeft, barWidth, startDay, endDay) => {
    if (startDay == null || endDay == null) return null;
    const totalDays = Math.max(endDay - startDay + 1, 1);
    const clickOffset = clickX - barLeft;
    const dayOffset = Math.floor((clickOffset / barWidth) * totalDays);
    return startDay + dayOffset;
};

const TemplateGantt = ({
    tasks = [],
    links = [],
    subtasks = [],
    statuses = [],
    epics = [],
    onTaskClick,
    readOnly = false,
    onTaskUpdate,
    onParentTaskMove,
    onDependencyCreate,
    onDependencyDelete,
    onDependencyColorChange,
    onSubtaskOrderChange,
    onMainTaskOrderChange,
    teamSwimlaneId,
    taskStatuses = [],
    onStatusChange,
    weekendWork = {},
    onWeekendWorkChange,
    blockedTasksMap = new Map(),
    members = [],
    onAssigneeChange,
    onClosureRequired,
    // T475/T471 - effort. A template shows the ESTIMATE ONLY (D5): a blueprint
    // is never worked on. Defaults to false for the same reason GanttChart's
    // does - fail closed.
    showEffort = false,
}) => {
    // Shared Gantt UI state from custom hook
    const {
        scrollContainerRef, containerRef, headerRef,
        containerWidth, headerHeight,
        currentScale, setCurrentScale,
        zoomLevel, setZoomLevel, hasZoomForScale, hasAnyZoom,
        uiScale, setUiScale, persistUiScale,
        persistZoom, gestureActiveRef,
        expandedItems, setExpandedItems,
        reorderDrag, setReorderDrag,
        dragState, setDragState,
        dragPreview, setDragPreview,
        pushedTaskPreview, setPushedTaskPreview,
        dragStateRef, dragPreviewRef, pushedTaskPreviewRef,
        positionOverridesRef,
        pendingLinks, setPendingLinks,
        editingDateRow, setEditingDateRow,
        dependencyDrag, setDependencyDrag,
        cursorPos, setCursorPos,
        dependencyDragRef,
        statusPopup, setStatusPopup,
        assigneePopup, setAssigneePopup,
        weekendPopup, setWeekendPopup,
        colorPopup, setColorPopup,
        topLinkKey, setTopLinkKey,
        tooltip, setTooltip,
        nameColWidth, setNameColWidth,
        colResizing, setColResizing,
        nameColCollapsed, setNameColCollapsed,
        datesCollapsed, setDatesCollapsed,
        viewMode, setViewMode,
        laneFilter, setLaneFilter,
        scrollPercent, setScrollPercent,
        effectiveLinks
    } = useGanttState('templateGantt', links, subtasks, tasks);


    // Every touch pointer currently down on the chart. ⛔ THE CHART OWNS ITS
    // IDENTITY and useGanttGestures is its only WRITER - the pan and the bar
    // drag both read it, and a second copy would answer a different question
    // from the one that armed the gesture.
    const pointersRef = useRef(new Map());

    // Drag the chart about by its free space. Nothing disables it here -
    // Follow mode is never on templates. isPanningRef feeds T352's guards.
    const { isPanningRef } = useGanttPan(scrollContainerRef, {
        disabled: false,
        pointersRef
    });

    // The device that produced the last pointer event, for the hover guard.
    const lastPointerTypeRef = useRef('mouse');

    // ⚠️ TemplateGantt has no `anyDragActive` of its own, so the pinch's block is
    // assembled from the drag states this chart does carry. It is passed as a
    // REQUIRED ref rather than defaulted - the useGanttPan precedent: a chart
    // that forgot it should fail visibly, not quietly pinch mid-drag.
    const gestureBlockedRef = useRef(false);
    gestureBlockedRef.current = !!(dragState || dependencyDrag || reorderDrag || colResizing);

    const zoomRef = useRef(zoomLevel);
    zoomRef.current = zoomLevel;
    const uiScaleRef = useRef(uiScale);
    uiScaleRef.current = uiScale;
    const pendingScrollTopRef = useRef(null);

    // Stable wrappers over handlers that read values changing every few renders
    // - passed directly, the gesture effect would re-attach its listeners
    // constantly and a wheel gesture would lose its idle timer each time.
    const gestureFnsRef = useRef({});
    gestureFnsRef.current = {
        onZoom: (next, clientX) => {
            const el = scrollContainerRef.current;
            if (el) {
                const rect = el.getBoundingClientRect();
                // From the TIMELINE's left edge: the left panel is sticky and
                // does not scroll.
                const offset = Math.max(0, clientX - rect.left - leftPanelWidth);
                const ratio = next / (zoomRef.current || 1);
                pendingScrollRef.current = anchoredScaleScroll(el.scrollLeft, offset, ratio);
            }
            setZoomLevel(next);
        },
        onUiScale: (next) => {
            const el = scrollContainerRef.current;
            if (el) {
                const ratio = next / (uiScaleRef.current || 1);
                // Offset 0 - anchor the TOP EDGE. This axis is a list.
                pendingScrollTopRef.current = anchoredScaleScroll(el.scrollTop, 0, ratio);
            }
            setUiScale(next);
        },
        onGestureStart: () => { gestureActiveRef.current = true; },
        onGestureEnd: () => {
            // Persist, THEN clear, and no state write after the clear.
            persistZoom();
            persistUiScale();
            gestureActiveRef.current = false;
        }
    };

    const onGestureZoom = useCallback((v, x) => gestureFnsRef.current.onZoom(v, x), []);
    const onGestureUiScale = useCallback((v) => gestureFnsRef.current.onUiScale(v), []);
    const onGestureStart = useCallback(() => gestureFnsRef.current.onGestureStart(), []);
    const onGestureEnd = useCallback(() => gestureFnsRef.current.onGestureEnd(), []);

    useGanttGestures(scrollContainerRef, {
        disabled: false,
        pointersRef,
        blockedRef: gestureBlockedRef,
        zoomRef,
        uiScaleRef,
        onZoom: onGestureZoom,
        onUiScale: onGestureUiScale,
        onGestureStart,
        onGestureEnd
    });

    // Component-specific refs
    const didDragRef = useRef(false);
    const pixelsPerDayRef = useRef(0);
    const tooltipTimeoutRef = useRef(null);

    // ⛔ ONE COPY OF THE CLAMP-AND-FLIP, SHARED WITH THE OTHER CHART AND WITH THE
    // DRAG CONFIRMATION (T403). This was an identical 25-line effect in both
    // Gantts and was about to be a third copy in the confirmation; the flip is
    // the case that would have drifted, and it is the one that matters.
    //
    // ⚠️ The clearer is what CustomTooltip's own onMouseLeave calls. The
    // placement already dies with its anchor, so this is belt and braces - but
    // the prop is not optional and a no-op passed in its place would be a lie.
    const { ref: tooltipRef, placed: tooltipPos, clear: clearTooltipPos } =
        useAnchoredPlacement(tooltip);

    // Every drag hides both hover cards (Bill, 2026-08-24). The bar tooltip is a
    // 700ms timer, so it lands when you pause mid-drag without letting go. The
    // dependency-line card is INSTANT and drags the chain dimming with it, so
    // merely crossing an arrow flashes every unrelated bar to 25%.
    //
    // Both halves below are needed. The guards stop a new card arming while a
    // drag runs; the effect kills one that is ALREADY showing, which a bar drag
    // always has - it starts from a hover, so the card is up before the button
    // goes down and no guard would ever remove it.
    //
    // No kickOffDragging here - templates have no kick-off marker.
    const anyDragActive = !!(
        dragState || dependencyDrag || reorderDrag || colResizing
    );

    useEffect(() => {
        if (!anyDragActive) return;
        if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
        setTooltip(null);
        setLinkTip(null);
        // The three setters are stable and listing them says nothing; what this
        // effect watches is the drag STARTING, and only that.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anyDragActive]);

    // Measures bar-name text for the weekend-gap word flow. document.body
    // because no element overrides the family and the container ref is null on
    // first render.
    //
    // 600 18px - the weight AND size the label renders at. It ran a deliberate
    // 1px behind (17px, Bill 2026-08-23) so the flow packed slightly tight, but
    // the overpacked text painted over the › chevron's air on weekend-gapped
    // bars with long names, so the two were put back in step (Bill, 2026-08-30).
    const nameMeasure = useMemo(
        // ⛔ AND IT MOVES WITH uiScale (T372). The label renders at
        // calc(18px * var(--gantt-scale)), so a fixed-size measurer
        // under-measures every name by the scale factor and the text overflows
        // the bars - SILENTLY, because jsdom measures nothing.
        () => makeTextMeasurer(`600 ${18 * uiScale}px ${getComputedStyle(document.body).fontFamily}`),
        [uiScale]
    );

    // The percentage label's own measurer (T397) - 18px, plain weight, matching
    // what that span actually renders. Erring tight here would SHOW a label that
    // does not fit, which is the defect being removed.
    const percentMeasure = useMemo(
        () => makeTextMeasurer(`${18 * uiScale}px ${getComputedStyle(document.body).fontFamily}`),
        [uiScale]
    );

    // And the bar name's own (T392) - 600 weight at the label's real 18px.
    // Same font string as nameMeasure since 2026-08-30, kept separate on
    // purpose. Three labels, three measurers.
    const barNameMeasure = useMemo(
        () => makeTextMeasurer(`600 ${18 * uiScale}px ${getComputedStyle(document.body).fontFamily}`),
        [uiScale]
    );

    const rowsRef = useRef([]);

    // Footer slider, mid-drag. Imperative ONLY - deliberately no setState.
    // scrollPercent is read in exactly one place (the slider's own value), so
    // updating it here repainted every bar and every dependency arrow for a
    // value the chart never consumes.
    const handleSliderScrub = (percent) => {
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer) return;
        const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
        scrollContainer.scrollLeft = (percent / 100) * maxScroll;
    };

    // Footer slider, on release. One re-render per drag instead of one per step.
    const handleSliderCommit = (percent) => setScrollPercent(percent);

    // Handle Fit button - calculate zoom to fit visible rows
    const handleFit = () => {
        // Fit = zoom so the subtasks currently on screen fill the timeline
        // width exactly. Template variant: relative day numbers, month = 28.
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer || rows.length === 0) return;

        // Fit rule: off-screen RIGHT and LEFT ignored, off-screen ABOVE and
        // BELOW included. Every subtask whose bar overlaps the current day
        // window counts, whatever row it is in. Nothing in window = no-op.
        const rowWindow = rows.filter(r => r.type === 'subtask' && (r.start != null || r.end != null));

        const { pxPerDay: curPxPerDay } = getColumnMetrics(currentScale, zoomLevel || 1, TEMPLATE_MONTH_DAYS);
        const winStartDays = scrollContainer.scrollLeft / curPxPerDay;
        const winEndDays = (scrollContainer.scrollLeft + scrollContainer.clientWidth) / curPxPerDay;

        const candidates = rowWindow.filter(r => {
            const sDays = (r.start != null ? r.start : r.end) - dateRange.minDay;
            const eDays = (r.end != null ? r.end : r.start) - dateRange.minDay + 1;
            return eDays >= winStartDays && sDays <= winEndDays;
        });
        if (candidates.length === 0) return;

        let minDay = null;
        let maxDay = null;
        for (const row of candidates) {
            if (row.start != null && (minDay === null || row.start < minDay)) minDay = row.start;
            if (row.end != null && (maxDay === null || row.end > maxDay)) maxDay = row.end;
        }
        if (minDay === null || maxDay === null) {
            minDay = dateRange.minDay;
            maxDay = dateRange.maxDay;
        }
        if (minDay == null || maxDay == null) return;

        const spanDays = (maxDay - minDay + 1) + 2;

        const { baseWidth, daysPerCol } = getColumnMetrics(currentScale, 1, TEMPLATE_MONTH_DAYS);
        const viewW = scrollContainer.clientWidth - leftPanelWidth;
        if (viewW <= 0 || spanDays <= 0) return;

        const fitZoom = Math.max(0.25, Math.min(8, (viewW * daysPerCol) / (spanDays * baseWidth)));

        const newPxPerDay = (baseWidth * fitZoom) / daysPerCol;
        const offsetDays = minDay - 1 - dateRange.minDay;
        const targetScroll = Math.max(0, offsetDays * newPxPerDay);
        if (Math.abs(fitZoom - (zoomLevel || 1)) < 0.001) {
            // Already at the fit zoom: a same-value setZoomLevel is a render
            // no-op and the layout effect never fires, so scroll directly - but
            // still write it, because the per-scale map uses presence to mean
            // "this scale has been fitted".
            setZoomLevel(fitZoom);
            scrollContainer.scrollLeft = targetScroll;
        } else {
            pendingScrollRef.current = targetScroll;
            setZoomLevel(fitZoom);
        }
    };

    // All = the ENTIRE template's subtasks zoomed to the closest fit.
    //
    // ⛔ THE HORIZONTAL HALF ALONE, AND THE AUTO-FIT EFFECT CALLS THIS ONE
    // (T386). ALL fits both axes now, but the auto-fit effect below is not a
    // button press - it runs on first load and on the first visit to each scale.
    // Point it at the two-axis handleFitAll and every first load silently
    // overwrites the reading size the user chose and persisted.
    const fitAllHorizontal = () => {
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer || rows.length === 0) return;

        let candidates = rows.filter(r => r.type === 'subtask' && (r.start != null || r.end != null));
        if (candidates.length === 0) candidates = rows.filter(r => r.start != null || r.end != null);
        if (candidates.length === 0) return;

        let minDay = null;
        let maxDay = null;
        for (const row of candidates) {
            if (row.start != null && (minDay === null || row.start < minDay)) minDay = row.start;
            if (row.end != null && (maxDay === null || row.end > maxDay)) maxDay = row.end;
        }
        if (minDay == null || maxDay == null) return;

        const spanDays = (maxDay - minDay + 1) + 2;
        const { baseWidth, daysPerCol } = getColumnMetrics(currentScale, 1, TEMPLATE_MONTH_DAYS);
        const viewW = scrollContainer.clientWidth - leftPanelWidth;
        if (viewW <= 0 || spanDays <= 0) return;

        const fitZoom = Math.max(0.25, Math.min(8, (viewW * daysPerCol) / (spanDays * baseWidth)));
        const newPxPerDay = (baseWidth * fitZoom) / daysPerCol;
        const offsetDays = minDay - 1 - dateRange.minDay;
        const targetScroll = Math.max(0, offsetDays * newPxPerDay);
        if (Math.abs(fitZoom - (zoomLevel || 1)) < 0.001) {
            // Same value, but it must still be WRITTEN: the per-scale map uses
            // presence to mean "this scale has been fitted". Skipping the write
            // left the scale marked unvisited for ever, stuck at zoom 1.
            setZoomLevel(fitZoom);
            scrollContainer.scrollLeft = targetScroll;
        } else {
            pendingScrollRef.current = targetScroll;
            setZoomLevel(fitZoom);
        }
    };

    // The vertical half. Rows as RENDERED: collapse and the lane filter are both
    // respected, and nothing is expanded to make the fit "complete".
    //
    // ⚠️ No Follow mode in this chart, so there is no autoScrollUntilRef stamp
    // here - the one difference from GanttChart's copy.
    const fitAllVertical = () => {
        const el = scrollContainerRef.current;
        if (!el || rows.length === 0) return;
        // This file's own header-height idiom, not a new fallback. headerHeight
        // is a ResizeObserver measurement and is 0 for the first frames.
        const header = headerHeight || (currentScale !== 'month' ? 64 : 36);
        const next = fitUiScale(el.clientHeight - header, rows.length, 36);
        if (next === null) return;

        if (next === uiScale) {
            // Already at the fit: no re-render is coming, so the layout effect
            // will not run and the scroll has to be written here.
            el.scrollTop = 0;
            return;
        }

        // ⛔ DEFERRED. Rows are only their new height AFTER the render.
        pendingScrollTopRef.current = 0;
        setUiScale(next);
        // A click, not a continuous gesture: durable at once, with an EXPLICIT
        // value because uiScaleRef still holds the pre-click one this tick.
        persistUiScale(next);
    };

    // The BUTTON fits both axes (Bill, 2026-08-30).
    const handleFitAll = () => {
        fitAllHorizontal();
        fitAllVertical();
    };

    // Template-specific date helpers for row building
    const templateDateHelpers = {
        taskStatuses,
        getEffectiveDates: (item) => ({
            start: item.relativeStart != null ? item.relativeStart : null,
            end: item.relativeEnd != null ? item.relativeEnd : null
        }),
        calculateSummaryDates: (children) => {
            let minStart = null;
            let maxEnd = null;
            let totalProgress = 0;
            let progressCount = 0;

            for (const child of children) {
                if (child.start != null) {
                    if (minStart === null || child.start < minStart) minStart = child.start;
                }
                if (child.end != null) {
                    if (maxEnd === null || child.end > maxEnd) maxEnd = child.end;
                }
                if (typeof child.progress === 'number') {
                    totalProgress += child.progress;
                    progressCount++;
                }
            }

            return {
                start: minStart,
                end: maxEnd,
                progress: progressCount > 0 ? Math.round(totalProgress / progressCount) : 0
            };
        },
        calculateDateRange: (rowList) => {
            let maxDay = 1;

            for (const r of rowList) {
                if (r.end != null && r.end > maxDay) maxDay = r.end;
            }

            // Add padding (2 weeks)
            maxDay = maxDay + 14;

            return { minDay: 1, maxDay };
        }
    };

    // Build hierarchical row data
    //
    // `builtCount` is the row count BEFORE the view/lane filter, and the two
    // must be told apart (2026-08-24). An empty TEMPLATE and a template whose
    // rows were all filtered away look identical from `rows.length` alone, and
    // treating them the same is what trapped Bill in prod: see the empty-state
    // branch below.
    const { rows, dateRange, builtCount } = useMemo(() => {
        // Flat views build with their own expansion (a collapsed group would
        // stop its tasks being built), then filter the tree down - see
        // gantt/viewModes.js.
        const built = buildGanttRows(tasks, statuses, epics, subtasks, expansionFor(viewMode, expandedItems), templateDateHelpers);
        return {
            rows: applyViewMode(built.rows, viewMode, laneFilter, teamSwimlaneId),
            dateRange: built.dateRange,
            builtCount: built.rows.length
        };
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [tasks, statuses, epics, subtasks, expandedItems, viewMode, laneFilter, teamSwimlaneId]
    );

    // Keep rowsRef in sync for use inside event handler closures
    useEffect(() => {
        rowsRef.current = rows;
    }, [rows]);

    // Percent of the scroll range that ONE DAY represents - the footer slider's
    // step. 100 means "nothing to scroll", so the slider sits at one end.
    // Declared here, next to the effect that maintains it: this file evaluates
    // dependency arrays during render, so state used by an effect must not be
    // declared below it.
    const [sliderStep, setSliderStep] = useState(100);

    // One footer-slider notch = one day. The input's domain is percent, so the
    // step is the percent one day is worth. Left to the HTML default of 1 it
    // moved 1% of the whole range, which reads as a broken day scale.
    //
    // maxScroll only moves with zoom, scale or row count, never with scrolling.
    useEffect(() => {
        const el = scrollContainerRef.current;
        if (!el) return;
        const { pxPerDay } = getColumnMetrics(currentScale, zoomLevel || 1, TEMPLATE_MONTH_DAYS);
        const maxScroll = el.scrollWidth - el.clientWidth;
        const scrollableDays = pxPerDay > 0 ? maxScroll / pxPerDay : 0;
        setSliderStep(scrollableDays >= 1 ? 100 / scrollableDays : 100);
    }, [currentScale, zoomLevel, rows.length, dateRange, scrollContainerRef]);

    // Auto-fit whenever the CURRENT SCALE has no stored zoom - first load, and
    // the first visit to each scale thereafter. This is the old isFirstLoad
    // effect widened: a single boolean could not answer "have I fitted THIS
    // scale yet", and adding a second effect would have raced this one on mount.
    // handleFitAll stores the (clamped) result, so it runs once per scale.
    useEffect(() => {
        if (hasZoomForScale || rows.length === 0 || !scrollContainerRef.current) return;
        // Small delay to ensure DOM is fully rendered
        const timer = setTimeout(() => {
            // Frames the WHOLE project (Bill, 2026-07-29)
            //
            // ⛔ THE HORIZONTAL HALF ONLY (T386). This is not a button press,
            // and the two-axis version would overwrite the user's persisted
            // reading size on every first load.
            fitAllHorizontal();
        }, 100);
        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows.length, currentScale, hasZoomForScale, hasAnyZoom]);

    // Toggle expand/collapse
    const toggleExpand = (rowId) => {
        setExpandedItems(prev => {
            const currentState = prev[rowId] !== false;
            return {
                ...prev,
                [rowId]: !currentState
            };
        });
    };

    // Bulk expand/collapse by category
    const toggleAllByType = (type, swimlaneFilter = null) => {
        setExpandedItems(prev => {
            const updates = {};
            let hasAnyExpanded = false;

            if (type === 'status') {
                statuses.forEach(status => {
                    const key = `status_${status.id}`;
                    if (prev[key] !== false) hasAnyExpanded = true;
                    updates[key] = null;
                });

                const epicIds = new Set();
                tasks.forEach(task => {
                    if (task.statusId) epicIds.add(task.statusId);
                });

                epicIds.forEach(epicId => {
                    const key = `epic_${epicId}`;
                    if (prev[key] !== false) hasAnyExpanded = true;
                    updates[key] = null;
                });
            } else if (type === 'task') {
                tasks.forEach(task => {
                    if (swimlaneFilter === 'team' && task.swimlaneId !== teamSwimlaneId) return;
                    if (swimlaneFilter === 'tme' && task.swimlaneId === teamSwimlaneId) return;
                    const key = `task_${task.projectId || 'default'}_${task.taskId}`;
                    if (prev[key] !== false) hasAnyExpanded = true;
                    updates[key] = null;
                });
            }

            const newState = hasAnyExpanded ? false : true;
            Object.keys(updates).forEach(key => {
                updates[key] = newState;
            });

            return { ...prev, ...updates };
        });
    };

    // Handle row reorder via drag-and-drop
    const handleRowReorder = (dragInfo) => {
        if (!dragInfo.orderKey) return;
        if (!dragInfo.targetRowId) return;
        if (dragInfo.sourceRowId === dragInfo.targetRowId) return;

        const orderKey = dragInfo.orderKey;
        let currentItems;
        let isMainTaskReorder = false;

        if (orderKey === '_statuses') {
            currentItems = rows.filter(r => r.type === 'status');
        } else if (orderKey === '_epics') {
            currentItems = rows.filter(r => r.type === 'epic');
        } else if (orderKey === '_root') {
            currentItems = rows.filter(r => r.type === 'task' && r.orderKey === '_root');
            isMainTaskReorder = true;
        } else if (orderKey.startsWith('_status_')) {
            currentItems = rows.filter(r => r.type === 'task' && r.orderKey === orderKey);
            isMainTaskReorder = true;
        } else if (orderKey.startsWith('_epic_')) {
            currentItems = rows.filter(r => r.type === 'task' && r.orderKey === orderKey);
            isMainTaskReorder = true;
        } else {
            currentItems = rows.filter(r => r.type === 'subtask' && String(r.mainTaskId) === String(orderKey) && r.projectId === dragInfo.projectId);
        }

        if (isMainTaskReorder && !onMainTaskOrderChange) return;
        if (!isMainTaskReorder && !onSubtaskOrderChange) return;

        const currentOrder = isMainTaskReorder
            ? currentItems.map(s => `${s.projectId}:${s.originalId}`)
            : currentItems.map(s => s.originalId);

        const sourceKey = isMainTaskReorder
            ? `${dragInfo.projectId}:${dragInfo.sourceOriginalId}`
            : dragInfo.sourceOriginalId;
        const targetRow = currentItems.find(r => r.id === dragInfo.targetRowId);
        const targetKey = isMainTaskReorder
            ? targetRow ? `${targetRow.projectId}:${targetRow.originalId}` : null
            : dragInfo.targetOriginalId;

        const sourceIdx = currentOrder.indexOf(sourceKey);
        const targetIdx = currentOrder.indexOf(targetKey);
        if (sourceIdx === -1 || targetIdx === -1) return;

        const [removed] = currentOrder.splice(sourceIdx, 1);
        currentOrder.splice(targetIdx, 0, removed);

        if (isMainTaskReorder && onMainTaskOrderChange) {
            const epicId = orderKey.split('_').pop();
            onMainTaskOrderChange(epicId, currentOrder);
        } else {
            onSubtaskOrderChange(orderKey, currentOrder, dragInfo.projectId);
        }
        setReorderDrag(null);
    };

    // Calculate columns based on date range and scale
    const { columns, weekSpans, pixelsPerDay, columnWidth } = useMemo(() => {
        const { minDay, maxDay } = dateRange;

        const cols = [];

        if (currentScale === 'day') {
            for (let d = minDay; d <= maxDay; d++) {
                const dayInWeek = ((d - 1) % 7) + 1;
                cols.push({
                    day: d,
                    label: `D${dayInWeek}`,
                    isWeekend: isTemplateWeekend(d),
                    week: Math.ceil(d / 7)
                });
            }
        } else if (currentScale === 'week') {
            // One column per 7 days
            for (let d = minDay; d <= maxDay; d += 7) {
                const week = Math.ceil(d / 7);
                cols.push({
                    day: d,
                    label: `W${week}`,
                    isWeekend: false,
                    week
                });
            }
        } else {
            // Month scale - one column per 28 days (4 weeks)
            for (let d = minDay; d <= maxDay; d += 28) {
                const startWeek = Math.ceil(d / 7);
                const endWeek = Math.ceil((d + 27) / 7);
                cols.push({
                    day: d,
                    label: `W${startWeek}-W${endWeek}`,
                    isWeekend: false,
                    week: startWeek
                });
            }
        }

        // Calculate column width based on zoom
        const { colWidth, pxPerDay } = getColumnMetrics(currentScale, zoomLevel, TEMPLATE_MONTH_DAYS);

        // Calculate week spans for the top header row (day and week scales only)
        const spans = [];
        if (currentScale === 'day' && cols.length > 0) {
            let currentWeek = cols[0].week;
            let spanStart = 0;
            let spanCount = 0;
            // Working days (D1-D5) lead every week, so the non-weekend columns
            // are always a contiguous prefix of the span - the band label
            // centres over THEM, not the 7-day span (Bill, 2026-08-21).
            let spanWorking = 0;

            for (let i = 0; i < cols.length; i++) {
                if (cols[i].week === currentWeek) {
                    spanCount++;
                    if (!cols[i].isWeekend) spanWorking++;
                } else {
                    spans.push({ label: `Week ${currentWeek}`, startIndex: spanStart, count: spanCount, workingCount: spanWorking });
                    currentWeek = cols[i].week;
                    spanStart = i;
                    spanCount = 1;
                    spanWorking = cols[i].isWeekend ? 0 : 1;
                }
            }
            spans.push({ label: `Week ${currentWeek}`, startIndex: spanStart, count: spanCount, workingCount: spanWorking });
        }

        return {
            columns: cols,
            weekSpans: spans,
            pixelsPerDay: pxPerDay,
            columnWidth: colWidth
        };
    }, [dateRange, currentScale, containerWidth, zoomLevel]);

    pixelsPerDayRef.current = pixelsPerDay;

    // Calculate bar position for a row using integer offsets
    const getBarPosition = (row) => {
        if (row.start == null) return null;

        const startOffset = row.start - dateRange.minDay;
        const duration = row.end != null ? Math.max(row.end - row.start + 1, 1) : 7;

        return {
            left: startOffset * pixelsPerDay,
            width: Math.max(duration * pixelsPerDay, 20)
        };
    };

    // T404. The question a finger's bar drag asks before anything is written -
    // the twin of GanttChart's, printing day numbers because a template has no
    // calendar. Component-local; nothing outside this chart reads it.
    const [dragConfirm, setDragConfirm] = useState(null);

    // Drag handlers
    const handleDragStart = (e, row) => {
        if (dependencyDragRef.current) return;
        if (readOnly) return;
        if (row.type === 'status' || row.type === 'epic') return;
        if (e.shiftKey) return;
        e.stopPropagation();
        e.preventDefault();

        const rect = e.currentTarget.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const barWidth = rect.width;

        // ⛔ PER EVENT, never per device: 44px for a finger, 12px for a mouse or
        // a pen. Both come from the same rect, which is why this chart scales by
        // multiplying its own numbers rather than by a CSS transform - under one,
        // a press on a bar's exact centre reads as resize-right.
        const dragType = dragTypeFor(clickX, barWidth, e.pointerType);

        const taskStart = row.start != null ? row.start : 1;
        const taskEnd = row.end != null ? row.end : taskStart + 6;

        const newDragState = {
            rowId: row.id,
            originalId: row.originalId,
            projectId: row.projectId,
            ref: row.ref,
            dragType,
            startX: e.clientX,
            originalStart: taskStart,
            originalEnd: taskEnd,
            // ⛔ THE ELEMENT, NOT ITS RECT (T404, §7.3). The confirmation is
            // anchored at RELEASE, and a rect measured now is stale by the whole
            // drag distance.
            anchorEl: e.currentTarget,
            // ⛔ For the release gate below: a TOUCH release that never travelled
            // past TOUCH_TAP_SLOP commits nothing. Mouse commits by deltaDays
            // alone, unchanged - GanttChart's twin carries the same pair.
            pointerType: e.pointerType,
            movedPastSlop: false
        };

        didDragRef.current = false;
        dragStateRef.current = newDragState;
        dragPreviewRef.current = null;
        setDragState(newDragState);
        setDragPreview(null);
    };

    // The five lines every end path runs. Named because there are now five of
    // them - the tap band, a cancelled gesture, a dependency drag, the
    // confirmation's Cancel and the ordinary finish.
    const clearDragPreviews = () => {
        setDragState(null);
        setDragPreview(null);
        dragPreviewRef.current = null;
        setPushedTaskPreview(null);
        pushedTaskPreviewRef.current = null;
    };

    // ⛔ THE SAME PLAN/APPLY SPLIT AS GanttChart (T404, §8.2), and the same rule:
    // planDragCommit writes nothing and prompts nothing. It is much smaller here
    // because a template has no calendar - no kick-off window, no weekend
    // alignment, and nothing external can shorten a parent move.
    const planDragCommit = (currentDragState, currentDragPreview) => {
        if (!currentDragState || !currentDragPreview) return null;

        const currentRows = rowsRef.current;
        const dragRow = currentRows.find(r => r.id === currentDragState.rowId);
        const base = {
            row: dragRow,
            dragType: currentDragState.dragType,
            // ⚠️ Day numbers, not dates - and off the DRAG state, which is the
            // only copy that still means "before".
            was: { start: currentDragState.originalStart, end: currentDragState.originalEnd },
            next: { start: currentDragPreview.start, end: currentDragPreview.end },
            overrideKey: `${currentDragState.projectId || 'default'}:${currentDragState.originalId}`,
            projectId: currentDragState.projectId,
            originalId: currentDragState.originalId,
            blocked: null,
            clampedBy: null
        };

        if (dragRow?.isSummary && dragRow?.type === 'task' && onParentTaskMove) {
            const parentSubtasks = currentRows.filter(r =>
                r.type === 'subtask' && r.mainTaskId === dragRow.originalId && r.projectId === dragRow.projectId
            );
            return {
                ...base,
                kind: 'parent',
                deltaDays: currentDragPreview.start - currentDragState.originalStart,
                // Only the subtasks that MOVE - the write loop below skips one
                // holding neither day, so counting it would overstate.
                affectedCount: parentSubtasks.filter(st => st.start != null || st.end != null).length,
                parentSubtasks,
                pushedTasks: null
            };
        }

        if (!onTaskUpdate) return null;

        const pushedTasks = pushedTaskPreviewRef.current;
        return {
            ...base,
            kind: 'single',
            deltaDays: null,
            affectedCount: pushedTasks ? pushedTasks.length : 0,
            parentSubtasks: null,
            pushedTasks
        };
    };

    const applyDragCommit = (plan) => {
        // ⛔ FIRST, BEFORE ANYTHING ELSE - it is what holds the bar in its new
        // place until the props catch up.
        positionOverridesRef.current[plan.overrideKey] = { ...plan.next };

        const dragRow = plan.row;
        const currentRows = rowsRef.current;

        if (plan.kind === 'parent') {
            plan.parentSubtasks.forEach(st => {
                if (st.start != null || st.end != null) {
                    const stNewStart = st.start != null ? st.start + plan.deltaDays : null;
                    const stNewEnd = st.end != null ? st.end + plan.deltaDays : null;
                    const stOverrideKey = `${st.projectId || 'default'}:${st.originalId}`;
                    positionOverridesRef.current[stOverrideKey] = {
                        start: stNewStart,
                        end: stNewEnd
                    };
                }
            });
            onParentTaskMove(dragRow.originalId, plan.deltaDays, plan.parentSubtasks);
        } else {
            // Commit pushed tasks FIRST, deepest first (reverse discovery
            // order), so every write is valid against the previous one
            const pushedTasks = plan.pushedTasks;
            if (pushedTasks && pushedTasks.length > 0) {
                [...pushedTasks].reverse().forEach(p => {
                    positionOverridesRef.current[`${p.projectId || 'default'}:${p.originalId}`] = {
                        start: p.start,
                        end: p.end
                    };
                    onTaskUpdate(p.originalId, {
                        relativeStart: p.start,
                        relativeEnd: p.end,
                        isSubtask: true,
                        projectId: p.projectId
                    });
                });
            }

            onTaskUpdate(plan.originalId, {
                relativeStart: plan.next.start,
                relativeEnd: plan.next.end,
                isSubtask: dragRow?.type === 'subtask',
                projectId: plan.projectId
            });

            // Update parent task position override if this is a subtask
            if (dragRow?.type === 'subtask' && dragRow?.mainTaskId) {
                const parentRow = currentRows.find(r => r.originalId === dragRow.mainTaskId && r.type === 'task' && r.projectId === dragRow.projectId);
                if (parentRow) {
                    const siblingSubtasks = currentRows.filter(r => r.type === 'subtask' && r.mainTaskId === dragRow.mainTaskId && r.projectId === dragRow.projectId);
                    let minStart = null;
                    let maxEnd = null;
                    for (const sibling of siblingSubtasks) {
                        const siblingStart = sibling.originalId === dragRow.originalId
                            ? plan.next.start
                            : sibling.start;
                        const siblingEnd = sibling.originalId === dragRow.originalId
                            ? plan.next.end
                            : sibling.end;
                        if (siblingStart != null && (minStart === null || siblingStart < minStart)) minStart = siblingStart;
                        if (siblingEnd != null && (maxEnd === null || siblingEnd > maxEnd)) maxEnd = siblingEnd;
                    }
                    if (minStart !== null && maxEnd !== null) {
                        const parentOverrideKey = `${parentRow.projectId || 'default'}:${dragRow.mainTaskId}`;
                        positionOverridesRef.current[parentOverrideKey] = {
                            start: minStart,
                            end: maxEnd
                        };
                    }
                }
            }
        }

        clearDragPreviews();
    };

    // ⛔ THE QUESTION DOES NOT OUTLIVE THE ROW IT IS ABOUT (D16). Two admins in
    // one template is the same stale save the client chart is protected from,
    // and Bill ruled it applies to both charts. Row-scoped: only the DRAGGED row
    // changing or vanishing dismisses it, compared against the pre-drag pair,
    // and never against positionOverridesRef - that is our own optimistic copy.
    //
    // ⚠️ Day numbers here, not date strings.
    useEffect(() => {
        if (!dragConfirm) return;
        const row = rows.find(r =>
            r.originalId === dragConfirm.originalId &&
            (r.projectId || null) === (dragConfirm.projectId || null)
        );
        if (row) {
            const start = row.start != null ? row.start : 1;
            const end = row.end != null ? row.end : start + 6;
            if (start === dragConfirm.was.start && end === dragConfirm.was.end) return;
        }
        setDragConfirm(null);
        clearDragPreviews();
        // What this watches is the ROW under an open question; clearDragPreviews
        // is redeclared every render and listing it would re-run this on each.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, dragConfirm]);

    // D4. The chart owns the wording; null renders no line at all.
    const dragConfirmCountLabel = !dragConfirm || dragConfirm.affectedCount === 0
        ? null
        : dragConfirm.kind === 'parent'
            ? `moves ${dragConfirm.affectedCount} subtask${dragConfirm.affectedCount === 1 ? '' : 's'}`
            : `${dragConfirm.affectedCount} linked task${dragConfirm.affectedCount === 1 ? '' : 's'} also move${dragConfirm.affectedCount === 1 ? 's' : ''}`;

    // Global mouse listeners for dragging
    useEffect(() => {
        if (!dragState) return;

        const handleMouseMove = (e) => {
            if (e.shiftKey || dependencyDragRef.current) return;

            const currentDragState = dragStateRef.current;
            if (!currentDragState) return;

            const deltaX = e.clientX - currentDragState.startX;
            if (Math.abs(deltaX) > 3) didDragRef.current = true;
            if (Math.abs(deltaX) > TOUCH_TAP_SLOP) currentDragState.movedPastSlop = true;
            const deltaDays = Math.round(deltaX / pixelsPerDayRef.current);

            let newStart = currentDragState.originalStart;
            let newEnd = currentDragState.originalEnd;

            if (currentDragState.dragType === 'move') {
                newStart = currentDragState.originalStart + deltaDays;
                newEnd = currentDragState.originalEnd + deltaDays;
                // Clamp: cannot drag before Day 1
                if (newStart < 1) {
                    const shift = 1 - newStart;
                    newStart = 1;
                    newEnd = newEnd + shift;
                }
            } else if (currentDragState.dragType === 'resize-left') {
                newStart = currentDragState.originalStart + deltaDays;
                if (newStart > newEnd) newStart = newEnd;
                if (newStart < 1) newStart = 1;
            } else if (currentDragState.dragType === 'resize-right') {
                newEnd = currentDragState.originalEnd + deltaDays;
                if (newEnd < newStart) newEnd = newStart;
            }

            // --- FS CONSTRAINTS (Bill's rules, 2026-07-29) ---
            // MOVE: clamp at the boundary, nothing else moves.
            // RESIZE: push - all links, full chain, gaps preserved.
            // Template variant: relative day numbers feed the engine directly.
            let pushedTasks = null;
            const currentRows = rowsRef.current;
            const activeRow = currentRows.find(r => r.id === currentDragState.rowId);
            if (activeRow && activeRow.type === 'subtask') {
                const tasksByKey = {};
                const rowByKey = {};
                for (const r of currentRows) {
                    if (r.type !== 'subtask') continue;
                    if (r.start == null && r.end == null) continue;
                    const k = `${r.ref}|${r.projectId || ''}`;
                    tasksByKey[k] = {
                        start: r.start != null ? r.start : r.end,
                        end: r.end != null ? r.end : r.start
                    };
                    rowByKey[k] = r;
                }
                const edges = [];
                for (const link of effectiveLinks) {
                    const sRow = currentRows.find(r =>
                        r.ref === getNumericRef(link.source) && r.type === 'subtask' &&
                        (!link.sourceProjectId || (r.sourceTemplateId || r.projectId) === link.sourceProjectId));
                    const tRow = currentRows.find(r =>
                        r.ref === getNumericRef(link.target) && r.type === 'subtask' &&
                        (!link.targetProjectId || (r.sourceTemplateId || r.projectId) === link.targetProjectId));
                    if (!sRow || !tRow) continue;
                    const sKey = `${sRow.ref}|${sRow.projectId || ''}`;
                    const tKey = `${tRow.ref}|${tRow.projectId || ''}`;
                    if (tasksByKey[sKey] && tasksByKey[tKey]) edges.push({ source: sKey, target: tKey });
                }
                const ctx = { tasksByKey, edges };
                const selfKey = `${activeRow.ref}|${activeRow.projectId || ''}`;

                if (ctx.tasksByKey[selfKey]) {
                    if (currentDragState.dragType === 'move') {
                        const clamped = clampMove(ctx, selfKey, newStart, newEnd);
                        newStart = clamped.start;
                        newEnd = clamped.end;
                    } else {
                        const { changes, order } = cascadeResize(ctx, selfKey, newStart, newEnd);
                        pushedTasks = order.slice(1).map(k => {
                            const row = rowByKey[k];
                            return {
                                rowId: row.id,
                                originalId: row.originalId,
                                projectId: row.projectId,
                                start: changes[k].start,
                                end: changes[k].end
                            };
                        });
                        if (pushedTasks.length === 0) pushedTasks = null;
                    }
                }
            }
            pushedTaskPreviewRef.current = pushedTasks;
            setPushedTaskPreview(pushedTasks);
            // --- END FS CONSTRAINTS ---

            const newPreview = {
                rowId: currentDragState.rowId,
                start: newStart,
                end: newEnd
            };
            dragPreviewRef.current = newPreview;
            setDragPreview(newPreview);
        };

        // ⛔ THE EVENT IS TAKEN NOW - it is the only live source of the release
        // point the confirmation is anchored on (§7.3).
        const handleMouseUp = (e) => {
            const currentDragState = dragStateRef.current;
            const currentDragPreview = dragPreviewRef.current;

            // ⛔ IDEMPOTENT: pointerup and lostpointercapture BOTH fire on an
            // ordinary release, so this runs twice every time.
            if (!currentDragState) return;

            dragStateRef.current = null;
            didDragRef.current = false;

            // ⛔ A TOUCH RELEASE INSIDE THE TAP BAND COMMITS NOTHING - at a
            // zoomed-out scale a day is a few pixels, so an ungated release
            // lets a finger wobble silently move a task. GanttChart's twin
            // carries the same gate.
            if (currentDragState.pointerType === 'touch' && !currentDragState.movedPastSlop) {
                clearDragPreviews();
                return;
            }

            if (dependencyDragRef.current) {
                clearDragPreviews();
                return;
            }

            const plan = planDragCommit(currentDragState, currentDragPreview);
            if (!plan) {
                clearDragPreviews();
                return;
            }

            // ⛔ A FINGER ASKS FIRST (T404) - the same question GanttChart asks,
            // printing W1D3 rather than a date because that is what a template
            // has. Only dragState is cleared: the previews stay so the bar sits
            // where the finger left it while the question is up.
            if (currentDragState.pointerType === 'touch') {
                setDragConfirm({
                    ...plan,
                    x: e.clientX,
                    y: currentDragState.anchorEl.getBoundingClientRect().top - 8
                });
                setDragState(null);
                return;
            }

            applyDragCommit(plan);
        };

        // ⛔ A CANCELLED GESTURE ABORTS - IT DOES NOT COMMIT (D11, 2026-08-30).
        // pointercancel is iOS taking the gesture away: the left-edge back
        // swipe, a system sheet, an incoming call. Until this was fixed it ran
        // handleMouseUp and WROTE the dates - and once the confirmation landed
        // it would have opened one anchored on a pointercancel's coordinates,
        // which browsers populate unreliably. GanttChart has aborted here all
        // along; this is the twin catching up.
        const handlePointerCancel = () => {
            if (!dragStateRef.current) return;
            dragStateRef.current = null;
            didDragRef.current = false;
            clearDragPreviews();
        };

        window.addEventListener('pointermove', handleMouseMove);
        window.addEventListener('pointerup', handleMouseUp);
        // ⛔ Both fire on an ordinary release, so the end path runs twice - it
        // returns early when the drag state is already null.
        window.addEventListener('lostpointercapture', handleMouseUp);
        window.addEventListener('pointercancel', handlePointerCancel);
        return () => {
            window.removeEventListener('pointermove', handleMouseMove);
            window.removeEventListener('pointerup', handleMouseUp);
            window.removeEventListener('lostpointercapture', handleMouseUp);
            window.removeEventListener('pointercancel', handlePointerCancel);
        };
    }, [dragState, onTaskUpdate]);

    // Dependency drag handlers
    const handleDependencyDragStart = (e, row) => {
        if (readOnly || row.type !== 'subtask') return;
        if (!e.shiftKey) return;

        e.preventDefault();
        e.stopPropagation();

        dragStateRef.current = null;
        dragPreviewRef.current = null;
        setDragState(null);
        setDragPreview(null);

        const rect = scrollContainerRef.current?.getBoundingClientRect();
        if (!rect) return;

        const newDependencyDrag = {
            sourceRowId: row.id,
            sourceOriginalId: row.originalId,
            sourceRef: row.ref,
            sourceText: row.text,
            sourceProjectId: row.sourceTemplateId || row.projectId,
            startX: e.clientX - rect.left + (scrollContainerRef.current?.scrollLeft || 0),
            startY: e.clientY - rect.top + (scrollContainerRef.current?.scrollTop || 0)
        };
        dependencyDragRef.current = newDependencyDrag;
        setDependencyDrag(newDependencyDrag);
        setCursorPos({
            x: e.clientX - rect.left + (scrollContainerRef.current?.scrollLeft || 0),
            y: e.clientY - rect.top + (scrollContainerRef.current?.scrollTop || 0)
        });
    };

    // Global handlers for dependency drag
    useEffect(() => {
        if (!dependencyDrag) return;

        const handleMouseMove = (e) => {
            const rect = scrollContainerRef.current?.getBoundingClientRect();
            if (!rect) return;
            setCursorPos({
                x: e.clientX - rect.left + (scrollContainerRef.current?.scrollLeft || 0),
                y: e.clientY - rect.top + (scrollContainerRef.current?.scrollTop || 0)
            });
        };

        const handleMouseUp = () => {
            dependencyDragRef.current = null;
            setDependencyDrag(null);
        };

        window.addEventListener('pointermove', handleMouseMove);
        window.addEventListener('pointerup', handleMouseUp);
        // ⛔ Both fire on an ordinary release, so the end path runs twice - it
        // returns early when the drag state is already null.
        window.addEventListener('lostpointercapture', handleMouseUp);
        window.addEventListener('pointercancel', handleMouseUp);
        return () => {
            window.removeEventListener('pointermove', handleMouseMove);
            window.removeEventListener('pointerup', handleMouseUp);
            window.removeEventListener('lostpointercapture', handleMouseUp);
            window.removeEventListener('pointercancel', handleMouseUp);
        };
    }, [dependencyDrag]);

    // Handle drop on a target subtask to create dependency
    const handleDependencyDrop = (e, targetRow) => {
        if (!dependencyDrag || readOnly) return;
        if (targetRow.type !== 'subtask') return;
        if (targetRow.id === dependencyDrag.sourceRowId) return;

        e.stopPropagation();
        e.preventDefault();

        dragStateRef.current = null;
        dragPreviewRef.current = null;

        const newLink = {
            source: dependencyDrag.sourceRef,
            target: targetRow.originalId,
            type: 'FS',
            sourceProjectId: dependencyDrag.sourceProjectId,
            targetProjectId: targetRow.sourceTemplateId || targetRow.projectId
        };

        setPendingLinks(prev => ({
            ...prev,
            added: [...prev.added, newLink]
        }));

        if (onDependencyCreate) {
            onDependencyCreate(newLink);
        }

        dependencyDragRef.current = null;
        setDependencyDrag(null);
    };

    // Build tooltip content
    const buildTooltipContent = (row) => {
        const lines = [];
        if (row.isMeeting) {
            lines.push(<div key="meeting-label"><span className="font-bold">Meeting:</span> {row.text}</div>);
            if (row.meetingType) {
                lines.push(<div key="meeting-type"><span className="font-bold">Type:</span> {row.meetingType}</div>);
            }
        } else {
            lines.push(<div key="name" className="font-bold">{row.text}</div>);
        }
        const startLabel = row.start != null ? formatRelativeDay(row.start) : 'No start';
        const endLabel = row.end != null ? formatRelativeDay(row.end) : 'No end';
        lines.push(<div key="dates">{startLabel} to {endLabel}</div>);
        if (row.assignee) {
            lines.push(<div key="assignee"><span className="font-bold">Assigned To:</span> {row.assignee}</div>);
        }

        if (row.links && row.links.length > 0) {
            lines.push(<div key="links-header" className="font-bold mt-1">Links:</div>);
            row.links.forEach((link, i) => {
                lines.push(
                    <div key={`link-${i}`} className="pl-2 truncate max-w-[250px]">
                        <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                            {link.description}
                        </a>
                    </div>
                );
            });
        }

        if (row.isMeeting && row.agenda) {
            lines.push(<div key="agenda-header" className="font-bold mt-1">Agenda:</div>);
            const agendaPreview = row.agenda.length > 150 ? row.agenda.substring(0, 150) + '...' : row.agenda;
            lines.push(<div key="agenda-content" className="pl-2 text-xs whitespace-pre-wrap max-w-[250px] mb-1">{agendaPreview}</div>);
        }

        if (row.type === 'subtask') {
            const parentTask = rows.find(r => r.type === 'task' && r.originalId === row.mainTaskId && r.projectId === row.projectId);
            if (parentTask) {
                // The whole tree, not just the main task (Bill, 2026-08-30).
                //
                // ⛔ parentTask.status is the GROUP's name here - the epic or
                // workflow status the main task sits under - NOT a workflow
                // status. The same field means the other thing one row down,
                // which is why statusLabel gates on row.type.
                const tree = parentTask.status
                    ? `${parentTask.status} > ${parentTask.text}`
                    : parentTask.text;
                lines.push(<div key="parent" className="mt-1"><span className="font-bold">Parent:</span> {tree}</div>);
            }
            // Bill: "directly after the Parent line". Omitted entirely when the
            // status resolves to nothing - a placeholder would read as a state.
            const statusName = statusLabel(row, taskStatuses);
            if (statusName) {
                lines.push(<div key="status"><span className="font-bold">Status:</span> {statusName}</div>);
            }
            const rowRef = row.ref;
            const deps = effectiveLinks.filter(l =>
                (getNumericRef(l.source) === rowRef && (!l.sourceProjectId || l.sourceProjectId === row.projectId)) ||
                (getNumericRef(l.target) === rowRef && (!l.targetProjectId || l.targetProjectId === row.projectId))
            );
            if (deps.length > 0) {
                lines.push(<div key="deps-header" className="font-bold mt-1">Dependencies:</div>);
                deps.forEach((d, i) => {
                    const isTarget = getNumericRef(d.target) === rowRef;
                    const otherId = isTarget ? getNumericRef(d.source) : getNumericRef(d.target);
                    const otherProjectId = isTarget ? d.sourceProjectId : d.targetProjectId;
                    const otherTask = rows.find(r =>
                        r.type === 'subtask' && r.ref === otherId &&
                        (!otherProjectId || (r.sourceTemplateId || r.projectId) === otherProjectId)
                    );
                    if (isTarget) {
                        lines.push(<div key={`dep-${i}`} className="pl-2"><span className="font-bold">Depends on:</span> {otherTask?.text || otherId} ({d.type || 'FS'})</div>);
                    } else {
                        lines.push(<div key={`dep-${i}`} className="pl-2"><span className="font-bold">Blocks:</span> {otherTask?.text || otherId} ({d.type || 'FS'})</div>);
                    }
                });
            }
            // Show blocked by info (isTemplateMode = true)
            const blockers = getBlockers(blockedTasksMap, row.originalId, row.projectId, true);
            if (blockers.length > 0) {
                lines.push(<div key="blocked-header" className="font-bold mt-1 text-red-400">Blocked by:</div>);
                blockers.forEach((b, i) => {
                    if (b.type === 'issue') {
                        lines.push(<div key={`blocker-${i}`} className="pl-2 text-red-300">Issue {b.id}: {b.title}</div>);
                    } else {
                        const depLabel = b.parentTitle ? `${b.parentTitle} / ${b.title}` : b.title;
                        lines.push(<div key={`blocker-${i}`} className="pl-2 text-red-300">{depLabel}</div>);
                    }
                });
            }
            if (!readOnly) {
                lines.push(<div key="hint" className="mt-2 text-gray-400 text-xs">Shift+drag to create dependency<br/>Ctrl+click weekend to toggle worked<br/>Ctrl+click dependency to pick colour</div>);
            }
        }
        // D20: a main task says which group it belongs to. NO Status line - a
        // main task has no workflow status of its own, and `row.status` here is
        // the group's name, so printing it as a status is the trap statusLabel
        // exists to close.
        if (row.type === 'task' && row.status) {
            lines.push(<div key="parent" className="mt-1"><span className="font-bold">Parent:</span> {row.status}</div>);
        }
        if (row.isSummary && row.type === 'task' && !readOnly) {
            lines.push(<div key="hint-summary" className="mt-2 text-gray-400 text-xs">Drag to move all subtasks</div>);
        }
        return lines;
    };

    // Tooltip mouse handlers
    const handleBarMouseEnter = (e, row) => {
        if (anyDragActive || isPanningRef.current) return;
        if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
        // x is the CURSOR, not the bar's midpoint: a bar zoomed wider than the
        // viewport has its midpoint off-screen, and the clamp below then pinned
        // the card against the screen edge - nowhere near the pointer. y stays
        // the bar's top, so the card still sits above it and the arrow flips.
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX;
        tooltipTimeoutRef.current = setTimeout(() => {
            setTooltip({
                content: buildTooltipContent(row),
                x,
                y: rect.top - 8
            });
        }, 700);
    };

    const handleBarMouseLeave = () => {
        if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
        tooltipTimeoutRef.current = setTimeout(() => {
            setTooltip(null);
        }, 100);
    };

    // Dependency-line hover card: own lightweight fixed card, NOT the
    // interactive bar tooltip - pointer-events none so it can never sit in
    // the way of clicking the line
    const [linkTip, setLinkTip] = useState(null);
    const handleLinkHover = (payload) => {
        // Only a card being SHOWN is blocked. The arrow's onMouseLeave calls this
        // with null (DependencyArrows.js:176) and that must always get through,
        // or a card shown before the drag began would stick there afterwards.
        if (payload && (anyDragActive || isPanningRef.current)) return;
        setLinkTip(payload);
    };

    // Rows whose bars stay bright while a dependency chain is hovered.
    //
    // The chain is subtask-level, so a subtask is in it when its node key
    // (projectId#ref - the same identity the arrow renderer chains on) is in the
    // hovered set. Its main task and epic stay bright with it: rows are emitted
    // epic > task > subtask in order, so the nearest preceding row of each type
    // is the parent. There is no project row in template mode.
    const chainRowIds = useMemo(() => {
        const nodes = linkTip?.nodes;
        if (!nodes || nodes.size === 0) return null;

        const bright = new Set();
        let lastEpicId = null;
        let lastTaskId = null;
        for (const row of rows) {
            if (row.type === 'status' || row.type === 'epic') lastEpicId = row.id;
            if (row.type === 'task') lastTaskId = row.id;
            if (row.type !== 'subtask') continue;
            const key = `${row.sourceTemplateId || row.projectId || ''}#${row.ref}`;
            if (!nodes.has(key)) continue;
            bright.add(row.id);
            if (lastTaskId) bright.add(lastTaskId);
            if (lastEpicId) bright.add(lastEpicId);
        }
        return bright;
    }, [linkTip, rows]);


    // Get display position (with drag preview or pushed task preview)
    const getDisplayPosition = (row) => {
        if (dragPreview && dragPreview.rowId === row.id) {
            const startOffset = dragPreview.start - dateRange.minDay;
            const duration = Math.max(dragPreview.end - dragPreview.start + 1, 1);
            return {
                left: startOffset * pixelsPerDay,
                width: Math.max(duration * pixelsPerDay, 20)
            };
        }
        if (pushedTaskPreview) {
            const pushed = pushedTaskPreview.find(p => p.rowId === row.id);
            if (pushed) {
                const startOffset = pushed.start - dateRange.minDay;
                const duration = Math.max(pushed.end - pushed.start + 1, 1);
                return {
                    left: startOffset * pixelsPerDay,
                    width: Math.max(duration * pixelsPerDay, 20),
                    isPushed: true
                };
            }
        }
        // Check for position override
        const overrideKey = `${row.projectId || 'default'}:${row.originalId}`;
        const override = positionOverridesRef.current[overrideKey];
        if (override) {
            const startOffset = override.start - dateRange.minDay;
            const duration = Math.max(override.end - override.start + 1, 1);
            return {
                left: startOffset * pixelsPerDay,
                width: Math.max(duration * pixelsPerDay, 20)
            };
        }
        return getBarPosition(row);
    };

    // Fixed column widths for left panel
    const COLLAPSED_COL_WIDTH = 20;
    const startDateColWidth = 100;
    const durationColWidth = 80;
    // Effective widths: a collapsed group is a 20px strip. Everything
    // downstream (bars, grid, band spacer, drop maths) rides leftPanelWidth.
    // Resize keeps the RAW nameColWidth - folding must not lose the width.
    const effNameColWidth = nameColCollapsed ? COLLAPSED_COL_WIDTH : nameColWidth;
    const effDatesWidth = datesCollapsed ? COLLAPSED_COL_WIDTH : startDateColWidth + durationColWidth;
    // T475 - part of leftPanelWidth or every bar shifts left by its width.
    // ⛔ No effort column width - the left-panel column was vetoed by Bill
    // (2026-09-01). Bar label only (D15).
    const leftPanelWidth = effNameColWidth + effDatesWidth;
    const rawTimelineWidth = columns.length * columnWidth;
    const minTimelineWidth = Math.max(containerWidth - leftPanelWidth, 400);
    const timelineWidth = Math.max(rawTimelineWidth, minTimelineWidth);
    // ⛔ SCALED. The rows really are taller at a larger reading size - nothing
    // is magnified - so the row positions, the arrows' row maths and the drop
    // maths all have to agree with what was drawn. Rounded, so row tops stay on
    // whole pixels.
    const rowHeight = Math.round(36 * uiScale);

    // Below the reading-size floor for text, every label comes off - see
    // UI_SCALE_TEXT_MIN. One boolean, read by the bar labels and the left panel.
    const showText = ganttTextVisible(uiScale);

    // ⛔ AN EDITOR CANNOT SURVIVE ITS OWN CELL BEING HIDDEN (D14). This chart's
    // Start cell is a <select> of relative days, and its onChange blurs
    // immediately - so a PICKED day is committed and cannot be lost here, unlike
    // GanttChart's typed date. What remains is the stale state: an open,
    // untouched select unmounted by the size drop would leave editingDateRow
    // pointing at that row, and the editor would re-open on the way back up.
    useEffect(() => {
        if (!showText && editingDateRow !== null) setEditingDateRow(null);
    }, [showText, editingDateRow, setEditingDateRow]);

    // FS engine context from current rows + canonical links. Shared by the
    // drag constraints and the date-picker clamp. Day-number native.
    const buildFsCtx = () => {
        const tasksByKey = {};
        const rowByKey = {};
        for (const r of rows) {
            if (r.type !== 'subtask') continue;
            if (r.start == null && r.end == null) continue;
            const k = `${r.ref}|${r.projectId || ''}`;
            tasksByKey[k] = {
                start: r.start != null ? r.start : r.end,
                end: r.end != null ? r.end : r.start
            };
            rowByKey[k] = r;
        }
        const edges = [];
        for (const link of effectiveLinks) {
            const sRow = rows.find(r =>
                r.ref === getNumericRef(link.source) && r.type === 'subtask' &&
                (!link.sourceProjectId || (r.sourceTemplateId || r.projectId) === link.sourceProjectId));
            const tRow = rows.find(r =>
                r.ref === getNumericRef(link.target) && r.type === 'subtask' &&
                (!link.targetProjectId || (r.sourceTemplateId || r.projectId) === link.targetProjectId));
            if (!sRow || !tRow) continue;
            const sKey = `${sRow.ref}|${sRow.projectId || ''}`;
            const tKey = `${tRow.ref}|${tRow.projectId || ''}`;
            if (tasksByKey[sKey] && tasksByKey[tKey]) edges.push({ source: sKey, target: tKey });
        }
        return { ctx: { tasksByKey, edges }, rowByKey };
    };

    // Zoom anchoring: keep the date at the viewport centre fixed across a
    // zoom change instead of letting the chart fly off sideways
    const pendingScrollRef = useRef(null);
    const applyZoomAnchored = (newZoom) => {
        const el = scrollContainerRef.current;
        if (el) {
            const viewW = el.clientWidth;
            const timelineX = el.scrollLeft + viewW / 2 - leftPanelWidth;
            const ratio = newZoom / (zoomLevel || 1);
            pendingScrollRef.current = Math.max(0, timelineX * ratio + leftPanelWidth - viewW / 2);
        }
        setZoomLevel(newZoom);
    };
    useLayoutEffect(() => {
        if (pendingScrollRef.current !== null && scrollContainerRef.current) {
            scrollContainerRef.current.scrollLeft = pendingScrollRef.current;
            pendingScrollRef.current = null;
        }
        // The reading size's twin: rows are only their new height after this
        // render, so scrollHeight is only correct now.
        if (pendingScrollTopRef.current !== null && scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = pendingScrollTopRef.current;
            pendingScrollTopRef.current = null;
        }
    }, [zoomLevel, uiScale]);

    // Generate dropdown options for date editing
    const dateDropdownOptions = useMemo(() => {
        if (!dateRange.maxDay) return [];
        const options = [];
        for (let d = dateRange.minDay; d <= dateRange.maxDay; d++) {
            if (!isTemplateWeekend(d)) {
                options.push({ value: d, label: formatRelativeDay(d) });
            }
        }
        return options;
    }, [dateRange]);

    // ONLY for a template that genuinely has no rows to build. This branch has
    // no toolbar, so it must never be reachable by a FILTER - that was a dead
    // end in prod (Bill, 2026-08-24): selecting the Client lane on a template
    // with no client swimlane emptied `rows`, this early return replaced the
    // whole component including the Lane buttons, and the filter persists in
    // localStorage - so there was nothing left to click and a reload did not
    // help. A filtered-empty chart now renders in full, with the message in the
    // body, and the toolbar always survives (see the scroll container below).
    //
    // The guard is `builtCount`, not `rows.length`, precisely so a filter can
    // never reach it. The bare branch stays because with nothing built at all
    // `dateRange` is degenerate and the header/grid maths below has no window
    // to draw.
    if (builtCount === 0) {
        return (
            <div className="h-full flex items-center justify-center text-gray-500">
                No tasks to display
            </div>
        );
    }

    // Check if a status is "Needs Info"
    const isNeedsInfo = (status) => String(status || '').toLowerCase().includes('needs info');

    return (
        <div ref={containerRef} className="h-full flex flex-col bg-white rounded-lg shadow overflow-hidden">
            {/* Toolbar */}
            <div className="flex items-center px-4 py-2 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                <div className="flex-1 flex items-center gap-4">
                    <span className="text-sm font-medium text-gray-700">Scale:</span>
                    <div className="flex gap-1">
                        {['day', 'week', 'month'].map((scale) => (
                            <button
                                key={scale}
                                onClick={() => {
                                    setCurrentScale(scale);
                                }}
                                className={`px-3 py-1 text-xs font-medium rounded shadow-sm shadow-gray-500/60 transition-colors ${
                                    currentScale === scale
                                        ? 'bg-primary-600 text-white'
                                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                                }`}
                            >
                                {scale.charAt(0).toUpperCase() + scale.slice(1)}
                            </button>
                        ))}
                    </div>

                    {/* Expand/Collapse buttons */}
                    <div className="h-5 w-0.5 shrink-0 bg-gray-300 mx-2" />
                    <span className="text-sm font-medium text-gray-700">Expand/Collapse:</span>
                    <div className="flex gap-1">
                        <Tooltip text="Expand/Collapse all Epics (Status groups)">
                            <button
                                onClick={() => toggleAllByType('status')}
                                disabled={!!viewMode}
                                className="px-2 py-1 text-xs font-medium rounded bg-gray-200 text-gray-600 shadow-sm shadow-gray-500/60 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                            >
                                Epics
                            </button>
                        </Tooltip>
                        <Tooltip text="Expand/Collapse all Main Tasks">
                            <button
                                onClick={() => toggleAllByType('task')}
                                disabled={viewMode === 'st'}
                                className="px-2 py-1 text-xs font-medium rounded bg-gray-200 text-gray-600 shadow-sm shadow-gray-500/60 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                            >
                                Main Tasks
                            </button>
                        </Tooltip>
                        <Tooltip text="Expand/Collapse T.M.E. Tasks">
                            <button
                                onClick={() => toggleAllByType('task', 'tme')}
                                disabled={!!viewMode}
                                className="px-2 py-1 text-xs font-medium rounded bg-gray-200 text-gray-600 shadow-sm shadow-gray-500/60 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                            >
                                T.M.E. Tasks
                            </button>
                        </Tooltip>
                        <Tooltip text="Expand/Collapse Client Tasks">
                            <button
                                onClick={() => toggleAllByType('task', 'team')}
                                disabled={!!viewMode}
                                className="px-2 py-1 text-xs font-medium rounded bg-gray-200 text-gray-600 shadow-sm shadow-gray-500/60 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                            >
                                Client Tasks
                            </button>
                        </Tooltip>
                    </div>

                    {/* Flat views + lane filter - Planning/GANTT-FLAT-VIEWS-2026-08-21.md.
                        A view button clicked while active exits to the tree; the lane
                        filter composes with every view, the tree included. */}
                    <div className="h-5 w-0.5 shrink-0 bg-gray-300 mx-2" />
                    <span className="text-sm font-medium text-gray-700">View:</span>
                    <div className="flex gap-1">
                        {[['mt', 'MT'], ['mtst', 'MT+ST'], ['st', 'ST']].map(([mode, label]) => (
                            <button
                                key={mode}
                                onClick={() => setViewMode(viewMode === mode ? null : mode)}
                                aria-pressed={viewMode === mode}
                                className={`px-2 py-1 text-xs font-medium rounded shadow-sm shadow-gray-500/60 transition-colors ${
                                    viewMode === mode ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                    <span className="text-sm font-medium text-gray-700">Lane:</span>
                    <div className="flex gap-1">
                        {[['all', 'All'], ['tme', 'TME'], ['team', 'Client']].map(([lane, label]) => (
                            <button
                                key={lane}
                                onClick={() => setLaneFilter(lane)}
                                aria-pressed={laneFilter === lane}
                                className={`px-2 py-1 text-xs font-medium rounded shadow-sm shadow-gray-500/60 transition-colors ${
                                    laneFilter === lane ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                                }`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>
                {/* Colour key */}
                <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500">Key:</span>
                    {taskStatuses
                        .filter(s => !s.is_archived)
                        .sort((a, b) => a.order - b.order)
                        .map(status => (
                            <div key={status.id} className="flex items-center gap-1">
                                <div className="w-3 h-3 rounded" style={{ backgroundColor: getStatusColour(status.name, status.isClosed) }} />
                                <span className="text-xs text-gray-600">{status.name}</span>
                            </div>
                        ))
                    }
                    <div className="flex items-center gap-1 ml-2 pl-2 border-l border-gray-300">
                        <div className="w-3 h-3 rounded-sm bg-gray-100" style={{ borderLeft: '3px solid #3b82f6' }} />
                        <span className="text-xs text-gray-600">Client Task</span>
                    </div>
                </div>

                <div className="flex-1 flex items-center gap-2 justify-end">
                    {/* H.Zoom, because there are two zooms on this bar now (T377). */}
                    <span className="text-sm font-medium text-gray-700">H.Zoom:</span>
                    <button
                        onClick={() => applyZoomAnchored(Math.max(0.25, (zoomLevel || 1) - 0.25))}
                        className="w-7 h-7 flex items-center justify-center text-sm font-bold rounded bg-gray-200 text-gray-600 shadow-sm shadow-gray-500/60 hover:bg-gray-300"
                    >
                        -
                    </button>
                    <ZoomSlider zoomLevel={zoomLevel} onCommit={applyZoomAnchored} />
                    <button
                        onClick={() => applyZoomAnchored(Math.min(8, (zoomLevel || 1) + 0.25))}
                        className="w-7 h-7 flex items-center justify-center text-sm font-bold rounded bg-gray-200 text-gray-600 shadow-sm shadow-gray-500/60 hover:bg-gray-300"
                    >
                        +
                    </button>
                    <span className="text-xs text-gray-600 w-12 text-center">
                        {Math.round((zoomLevel || 1) * 100)}%
                    </span>
                    {/* V.Zoom - the reading size. ⛔ NO slider and NO +/- buttons:
                        it is driven by ctrl+wheel and an upright pinch, and a
                        third control here is the "three zooms on one screen" that
                        was refused. A readout that happens to be clickable. */}
                    <div className="h-5 w-0.5 shrink-0 bg-gray-300 mx-2" />
                    <span className="text-sm font-medium text-gray-700">V.Zoom:</span>
                    <Tooltip text="Reading size - ctrl+wheel or pinch upright. Click to reset to 100%">
                        <button
                            onClick={() => {
                                // A click already at 100% writes nothing: the
                                // default is the absence of the stored key.
                                if (uiScale === UI_SCALE_DEFAULT) return;
                                const el = scrollContainerRef.current;
                                if (el) {
                                    // Anchor the TOP EDGE, as the wheel does - a
                                    // reset from 250% otherwise drops the reader
                                    // onto empty chart.
                                    const ratio = UI_SCALE_DEFAULT / (uiScale || 1);
                                    pendingScrollTopRef.current =
                                        anchoredScaleScroll(el.scrollTop, 0, ratio);
                                }
                                setUiScale(UI_SCALE_DEFAULT);
                                persistUiScale(UI_SCALE_DEFAULT);
                            }}
                            className="px-2 h-7 flex items-center justify-center text-xs text-gray-600 rounded hover:bg-gray-200"
                        >
                            {Math.round(snapUiScale(uiScale) * 100)}%
                        </button>
                    </Tooltip>
                    <div className="h-5 w-0.5 shrink-0 bg-gray-300 mx-2" />
                    <Tooltip text="Fit the subtasks in the current day window">
                        <button
                            onClick={handleFit}
                            className="px-2 h-7 flex items-center justify-center text-xs font-bold rounded bg-gray-200 text-gray-600 shadow-sm shadow-gray-500/60 hover:bg-gray-300"
                        >
                            FIT
                        </button>
                    </Tooltip>
                    <Tooltip text="Fit the entire template">
                        <button
                            onClick={handleFitAll}
                            className="px-2 h-7 flex items-center justify-center text-xs font-bold rounded bg-gray-200 text-gray-600 shadow-sm shadow-gray-500/60 hover:bg-gray-300"
                        >
                            ALL
                        </button>
                    </Tooltip>
                </div>
            </div>

            {/* Main content */}
            {/* Vertical scrollbar only, matching GanttChart. The horizontal bar
                duplicated the footer date slider and the two fought, so the X
                axis is hidden on the container; nav buttons and the slider set
                scrollLeft directly, which still works on a hidden axis.
                Deliberately NOT .gantt-scroll - that hid BOTH scrollbars. */}
            <div
                ref={scrollContainerRef}
                className="flex-1 overflow-y-auto overflow-x-hidden"
                // Which device is in use right now, for the hover-card guard.
                // Both events, capture phase - see GanttChart.js for why
                // pointerdown alone is not enough on a hybrid device.
                onPointerDownCapture={(e) => { lastPointerTypeRef.current = e.pointerType; }}
                onPointerMoveCapture={(e) => { lastPointerTypeRef.current = e.pointerType; }}
            >
                {/* ⛔ --gantt-scale GOES HERE AND NOWHERE HIGHER (T371). On the
                    chart root it would scale the toolbar and the footer too, so
                    at 2.5x the controls would eat the viewport and there would be
                    no way to zoom back out except a reload. calc() and a custom
                    property, never `em`, and no var(--gantt-scale, 1) fallback -
                    an element outside this subtree should be FOUND. */}
                <div className="relative" style={{ minWidth: leftPanelWidth + timelineWidth, '--gantt-scale': uiScale }}>
                    {/* Header rows */}
                    {/* z-40, NOT z-20 (2026-08-24) - same fix as GanttChart.js, same
                        reason: each row's left panel is `sticky left-0 z-20` and comes
                        later in the DOM, so at equal z it painted OVER this header and
                        the column headings read as unlocked. See the fuller note there. */}
                    <div ref={headerRef} className="sticky top-0 z-40">
                        {/* Week spans row (for day scale) */}
                        {/* The week BAND. Same treatment as the client Gantt's month
                            band: site popup gray, h-6 explicit height - the label spans
                            are absolute, so nothing in-flow sizes the row without it. */}
                        {currentScale === 'day' && weekSpans.length > 0 && (
                            <div className="flex h-6 bg-gray-500 border-b border-gray-600">
                                <div className="sticky left-0 bg-gray-500 z-30 border-r border-gray-600" style={{ width: leftPanelWidth }}>
                                    <div className="px-3 py-1 font-medium text-sm text-gray-700">
                                    </div>
                                    {/* Task-name/dates divider through the band, in the band's
                                        own border colour - light-on-dark reads as a scratch
                                        (organise 93e7e8c, re-decided in 2026-08-21). Hidden
                                        with the handle when the name column is folded. */}
                                    {!nameColCollapsed && (
                                        <div className="absolute top-0 bottom-0 w-px bg-gray-600" style={{ left: effNameColWidth }} />
                                    )}
                                </div>
                                {/* Absolutely positioned off the same idx * columnWidth
                                    arithmetic as the column row, the grid layer and the
                                    bars - see the comment on the column row below. */}
                                <div className="relative self-stretch" style={{ width: timelineWidth }}>
                                    {weekSpans.map((span, idx) => (
                                        <div
                                            key={idx}
                                            className={`absolute top-0 bottom-0 border-r border-gray-500 ${idx % 2 === 0 ? 'bg-gray-500' : 'bg-[#848b98]'}`}
                                            style={{
                                                left: span.startIndex * columnWidth,
                                                width: span.count * columnWidth
                                            }}
                                        >
                                            {/* Centred over the WORKING days only - the weekend
                                                columns at the week's end are excluded, or the
                                                label sits visually off-centre of the real week */}
                                            <div
                                                className="h-full flex items-center justify-center text-xs font-semibold text-white"
                                                style={{ width: (span.workingCount || span.count) * columnWidth }}
                                            >
                                                {span.label}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Day/Week/Month labels row */}
                        <div className="flex bg-gray-100 border-b border-gray-300">
                            {/* Left panel headers */}
                            <div className="sticky left-0 flex bg-gray-100 z-30 border-r border-gray-400">
                                {nameColCollapsed ? (
                                    <div className="flex items-center justify-center" style={{ width: COLLAPSED_COL_WIDTH }}>
                                        <button type="button" aria-label="Expand task name column" onClick={() => setNameColCollapsed(false)} className={COL_CHEVRON_CLASS}>›</button>
                                    </div>
                                ) : (
                                    <div
                                        className="relative px-3 py-2 font-medium text-sm text-gray-700"
                                        style={{ width: nameColWidth }}
                                    >
                                        <span className="flex items-center gap-2">
                                            <button type="button" aria-label="Collapse task name column" onClick={() => setNameColCollapsed(true)} className={COL_CHEVRON_CLASS}>‹</button>
                                            <span>Task Name</span>
                                        </span>
                                        {/* Task-name/dates divider (organise 93e7e8c ported,
                                            Bill re-decided 2026-08-21) */}
                                        <div className="absolute right-0 top-0 bottom-0 w-px bg-gray-400" />
                                        {/* Resize handle - 6px, straddling the divider line */}
                                        <div
                                            className="absolute right-0 top-0 bottom-0 w-1.5 translate-x-1/2 cursor-col-resize hover:bg-blue-400 bg-gray-300"
                                            style={{ zIndex: 50 }}
                                            onPointerDown={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                // ⛔ Capture, so a release outside
                                                // the window still ends the resize -
                                                // GanttChart's twin grip carries the
                                                // same guarded call.
                                                try {
                                                    e.currentTarget.setPointerCapture(e.pointerId);
                                                } catch (err) { /* pointer already gone */ }
                                                setColResizing({ startX: e.clientX, startWidth: nameColWidth });
                                            }}
                                        />
                                    </div>
                                )}
                                {datesCollapsed ? (
                                    <div className="flex items-center justify-center" style={{ width: COLLAPSED_COL_WIDTH }}>
                                        <button type="button" aria-label="Expand dates" onClick={() => setDatesCollapsed(false)} className={COL_CHEVRON_CLASS}>›</button>
                                    </div>
                                ) : (
                                    <>
                                        <div
                                            className="flex items-center px-2 py-2 font-medium text-sm text-gray-700 border-r border-gray-200"
                                            style={{ width: startDateColWidth }}
                                        >
                                            {/* In flow, not absolute - overlaid it collided with
                                                the centred label at every offset tried */}
                                            <button type="button" aria-label="Collapse dates" onClick={() => setDatesCollapsed(true)} className={COL_CHEVRON_CLASS}>‹</button>
                                            <span className="flex-1 text-center">Start</span>
                                        </div>
                                        <div
                                            className="px-3 py-2 font-medium text-sm text-gray-700 text-center"
                                            style={{ width: durationColWidth }}
                                        >
                                            Duration
                                        </div>
                                    </>
                                )}
                                {/* ⛔ NO EFFORT COLUMN - vetoed by Bill on
                                    sight (2026-09-01); it rode in on a
                                    mis-recorded D12. Bar label only (D15).
                                    Do not reinstate. */}
                            </div>

                            {/* Timeline column headers.
                                ABSOLUTELY POSITIONED, NOT A FLEX ROW. columnWidth is
                                baseWidth * zoomLevel and FIT sets a fractional zoom, so a
                                flex row of `width: columnWidth` boxes rounds every column
                                to device pixels and the error ACCUMULATES. The bars and
                                the grid layer use exact idx * columnWidth, so a rounded
                                header drifts away from them the further right you scroll.
                                Every strip now shares the same exact arithmetic. */}
                            <div className="relative self-stretch" style={{ width: timelineWidth }}>
                                {columns.map((col, idx) => {
                                    const isSaturday = col.isWeekend && col.day % 7 === 6;
                                    const isSunday = col.isWeekend && col.day % 7 === 0;
                                    return (
                                        <div
                                            key={idx}
                                            className={`absolute top-0 bottom-0 flex items-center justify-center text-xs font-medium px-1 py-2 text-center ${
                                                col.isWeekend ? 'bg-gray-200 text-gray-500' : 'text-gray-600'
                                            } ${
                                                isSaturday ? 'border-l border-l-gray-200' : ''
                                            } ${
                                                isSunday ? 'border-r border-r-gray-200' : 'border-r border-gray-100'
                                            }`}
                                            style={{ left: idx * columnWidth, width: columnWidth }}
                                        >
                                            {!(currentScale === 'day' && zoomLevel !== null && zoomLevel <= 0.5) && col.label}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Column grid - ONE layer for the entire chart, behind every row.
                        Was a div per day PER ROW, so the node count was columns x rows;
                        drawn once it is just columns. Uses the same exact
                        idx * columnWidth as the header strips and the bars, so it cannot
                        drift from either.

                        z-1 puts it above the rows' opaque backgrounds but below the bars
                        (z-10) and the dependency arrows (z-5). */}
                    <div
                        className="absolute pointer-events-none"
                        style={{
                            left: leftPanelWidth,
                            top: headerHeight || (currentScale !== 'month' ? 64 : 36),
                            width: timelineWidth,
                            height: rows.length * rowHeight,
                            zIndex: 1
                        }}
                    >
                        {columns.map((col, idx) => {
                            const isSaturday = col.isWeekend && col.day % 7 === 6;
                            const isSunday = col.isWeekend && col.day % 7 === 0;
                            return (
                                <div
                                    key={idx}
                                    className={`absolute top-0 bottom-0 ${
                                        col.isWeekend ? 'bg-gray-200/50' : ''
                                    } ${
                                        isSaturday ? 'border-l border-l-gray-200' : ''
                                    } ${
                                        isSunday ? 'border-r border-r-gray-200' : 'border-r border-gray-100'
                                    }`}
                                    style={{ left: idx * columnWidth, width: columnWidth }}
                                />
                            );
                        })}
                    </div>

                    {/* Data rows */}
                    {/* Filtered down to nothing. Rendered HERE, inside the chart,
                        rather than as an early return - the toolbar above must stay
                        on screen or the filter that emptied the chart cannot be
                        undone (Bill, 2026-08-24). It names the filter, because
                        "no tasks" on a template that plainly has tasks reads as
                        data loss. */}
                    {rows.length === 0 && (
                        <div className="flex items-center justify-center py-16 text-gray-500">
                            No tasks in this lane — use the Lane buttons above to change it
                        </div>
                    )}
                    {rows.map((row, rowIdx) => {
                        const pos = getDisplayPosition(row);
                        const isDragging = dragState?.rowId === row.id;
                        const isClientTask = row.type === 'task' && row.isSummary && teamSwimlaneId && row.swimlaneId === teamSwimlaneId;
                        const isAddinTask = row._isAddin === true;
                        const barColour = isAddinTask ? row._addinColour
                            : (row.type === 'status' || row.type === 'epic') ? STATUS_COLOUR
                            : (row.isSummary ? (isClientTask ? CLIENT_PARENT_COLOUR : PARENT_COLOUR) : getStatusColour(row.status, row.isClosed, row.isMeeting));
                        const indentPx = row.level * 20;

                        const rowBgClass = (row.type === 'status' || row.type === 'epic')
                            ? 'bg-purple-50'
                            : row.isSummary
                                ? (isClientTask ? 'bg-blue-50' : 'bg-green-50')
                                : rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50';

                        const nextRow = rows[rowIdx + 1];

                        const isInStatusGroup = row.type === 'status' || row.type === 'epic' || (row.parentStatusId !== undefined) || (row.parentEpicId !== undefined);
                        const isStatusGroupEnd = isInStatusGroup && (!nextRow || nextRow.type === 'status' || nextRow.type === 'epic' || (nextRow.type === 'task' && !nextRow.parentStatusId && !nextRow.parentEpicId));

                        const isMainTaskGroupEnd = row.type === 'subtask' && (!nextRow || nextRow.type !== 'subtask' || nextRow.mainTaskId !== row.mainTaskId);

                        let rowBorderClass = 'border-b border-gray-100';

                        const parentTask = row.type === 'subtask' ? rows.find(r => r.type === 'task' && r.originalId === row.mainTaskId && r.projectId === row.projectId) : null;
                        const isClientSubtask = parentTask && teamSwimlaneId && parentTask.swimlaneId === teamSwimlaneId;
                        const useBlue = isClientTask || isClientSubtask;

                        if (row.type === 'status' || row.type === 'epic') {
                            rowBorderClass = 'border-t border-t-purple-300 border-b border-b-gray-100';
                            if (isStatusGroupEnd) rowBorderClass = 'border-t border-t-purple-300 border-b border-b-purple-300';
                        } else if (row.type === 'task' && row.isSummary) {
                            if (useBlue) {
                                rowBorderClass = 'border-t border-t-blue-400 border-b border-b-gray-100';
                                if (!nextRow || nextRow.type !== 'subtask' || nextRow.mainTaskId !== row.originalId) {
                                    rowBorderClass = 'border-t border-t-blue-400 border-b border-b-blue-400';
                                }
                            } else {
                                rowBorderClass = 'border-t border-t-green-300 border-b border-b-gray-100';
                                if (!nextRow || nextRow.type !== 'subtask' || nextRow.mainTaskId !== row.originalId) {
                                    rowBorderClass = 'border-t border-t-green-300 border-b border-b-green-300';
                                }
                            }
                        } else if (row.type === 'subtask' && isMainTaskGroupEnd) {
                            rowBorderClass = useBlue ? 'border-b border-b-blue-400' : 'border-b border-b-green-300';
                        } else if (isInStatusGroup && isStatusGroupEnd) {
                            rowBorderClass = 'border-b border-b-purple-300';
                        }

                        const isTeamTask = row.type === 'task' && teamSwimlaneId && row.swimlaneId === teamSwimlaneId;

                        const rowStyle = { height: rowHeight };
                        if (row._isAddin && row._addinColour && (row.type === 'task' || row.type === 'subtask')) {
                            rowStyle.backgroundColor = row._addinColour + '20';
                        }

                        return (
                            <div
                                key={row.id}
                                className={`flex ${rowBorderClass} ${rowBgClass}`}
                                style={rowStyle}
                            >
                                {/* Left panel cells */}
                                <div
                                    className="sticky left-0 flex z-20 border-r border-gray-400"
                                    style={{
                                        boxShadow: isTeamTask ? 'inset 3px 0 0 0 #3b82f6' : 'none',
                                        background: row._isAddin && row._addinColour && (row.type === 'task' || row.type === 'subtask')
                                            ? `linear-gradient(${row._addinColour}20, ${row._addinColour}20), ${row.isSummary ? '#f0fdf4' : '#ffffff'}`
                                            : 'inherit'
                                    }}
                                >
                                    {/* Task Name with expand/collapse */}
                                    <div
                                        className={`flex items-center px-2 border-r border-gray-400 overflow-hidden ${
                                            reorderDrag?.targetRowId === row.id ? 'bg-blue-100' : ''
                                        }`}
                                        style={{ width: effNameColWidth }}
                                        onDragOver={(e) => {
                                            const canDrop = !readOnly && reorderDrag && (
                                                (row.orderKey && row.orderKey === reorderDrag.orderKey) ||
                                                (row.type === 'subtask' && row.mainTaskId && row.mainTaskId.toString() === reorderDrag.orderKey)
                                            );
                                            if (canDrop) {
                                                e.preventDefault();
                                                setReorderDrag(prev => ({ ...prev, targetRowId: row.id, targetOriginalId: row.originalId }));
                                            }
                                        }}
                                    >
                                        {!nameColCollapsed && <div style={{ width: indentPx }} className="flex-shrink-0" />}

                                        {/* Drag handle for reordering. The grip is exactly the
                                            20px strip, so it SURVIVES a folded name column -
                                            reorder keeps working collapsed (organise T162 rule).

                                            ⛔ AND IT GOES BELOW THE TEXT THRESHOLD (D5, T398): a
                                            fixed 20px control in an 11px row overflows into its
                                            neighbours. The else branch's spacer renders instead, so
                                            the indent holds and the panel does not reflow. */}
                                        {showText && !readOnly && !viewMode && (
                                            (onSubtaskOrderChange && (row.orderKey || row.mainTaskId)) ||
                                            (onMainTaskOrderChange && row.type === 'task' && row.orderKey)
                                        ) ? (
                                            // `fixed`: the name-column cell is overflow-hidden, which
                                            // clips the default CSS bubble. flex-shrink-0 moves to the
                                            // wrapper so the grip keeps its width in the flex row.
                                            <Tooltip text="Drag to reorder" fixed className="flex-shrink-0">
                                            <div
                                                draggable
                                                onDragStart={(e) => {
                                                    const orderKey = row.orderKey || (row.mainTaskId ? row.mainTaskId.toString() : null);
                                                    setReorderDrag({
                                                        sourceRowId: row.id,
                                                        sourceOriginalId: row.originalId,
                                                        orderKey: orderKey,
                                                        projectId: row.projectId
                                                    });
                                                    e.dataTransfer.effectAllowed = 'move';
                                                    e.dataTransfer.setData('text/plain', '');
                                                }}
                                                onDragEnd={() => {
                                                    if (reorderDrag?.targetOriginalId) {
                                                        handleRowReorder(reorderDrag);
                                                    } else {
                                                        setReorderDrag(null);
                                                    }
                                                }}
                                                className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-grab"
                                                aria-label="Drag to reorder"
                                            >
                                                &#8942;
                                            </div>
                                            </Tooltip>
                                        ) : (
                                            <div className="w-5 flex-shrink-0" />
                                        )}

                                        {!nameColCollapsed && (<>
                                        {/* Expand/collapse triangle. Same treatment as the grip
                                            above below the text threshold, for the same reason. */}
                                        {row.hasChildren && showText ? (
                                            <button
                                                onClick={() => toggleExpand(row.id)}
                                                className="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-gray-700 flex-shrink-0"
                                            >
                                                {row.isExpanded ? '\u25BC' : '\u25BA'}
                                            </button>
                                        ) : (
                                            <div className="w-5 flex-shrink-0" />
                                        )}

                                        {/* Task name. `fixed` because the cell is overflow-hidden;
                                            min-w-0 flex-1 moves to the wrapper so the button inside
                                            still truncates rather than pushing the row wide.

                                            \u26D4 BELOW THE TEXT THRESHOLD THE BUTTON GOES WITH ITS TEXT
                                            (D10): an invisible click target that opens a task is
                                            worse than none. The bare div keeps the flex geometry the
                                            Tooltip wrapper was providing. The BAR stays clickable. */}
                                        {showText ? (
                                        <Tooltip text={row.text} fixed className="min-w-0 flex-1">
                                        <button
                                            onClick={() => row.type !== 'status' && row.type !== 'epic' && onTaskClick?.(row.originalId)}
                                            className={`text-[calc(15px*var(--gantt-scale))] truncate text-left w-full ${
                                                (row.type === 'status' || row.type === 'epic')
                                                    ? 'font-semibold text-gray-800 cursor-default'
                                                    : row.isClosed
                                                        ? 'text-gray-400 line-through hover:text-gray-500'
                                                        : 'text-gray-700 hover:text-primary-600'
                                            }`}
                                        >
                                            {row.text}
                                            {row.childCount > 0 && (
                                                <span className="text-xs text-gray-400 ml-1">({row.childCount})</span>
                                            )}
                                        </button>
                                        </Tooltip>
                                        ) : (
                                            <div className="min-w-0 flex-1" />
                                        )}
                                        </>)}
                                    </div>

                                    {datesCollapsed ? (
                                        /* Folded dates: ONE 20px spacer keeps the panel's
                                           flex content matching its width */
                                        <div className="flex-shrink-0" style={{ width: COLLAPSED_COL_WIDTH }} />
                                    ) : (<>
                                    {/* Start position - dropdown for editable rows */}
                                    <div
                                        className={`flex items-center justify-center px-1 text-[calc(15px*var(--gantt-scale))] border-r border-gray-100 ${
                                            !readOnly && !row.isSummary && row.type !== 'status' && row.type !== 'epic'
                                                ? 'cursor-pointer hover:bg-blue-50 text-gray-700'
                                                : 'text-gray-600'
                                        }`}
                                        style={{ width: startDateColWidth }}
                                        onClick={() => {
                                            // D10: no invisible editor - below the threshold this
                                            // cell renders nothing at all.
                                            if (!showText) return;
                                            if (!readOnly && !row.isSummary && row.type !== 'status' && row.type !== 'epic') {
                                                setEditingDateRow(row.id);
                                            }
                                        }}
                                    >
                                        {showText && (editingDateRow === row.id ? (
                                            <select
                                                className="w-full text-[calc(15px*var(--gantt-scale))] border border-blue-400 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                defaultValue={row.start != null ? row.start : ''}
                                                autoFocus
                                                onBlur={(e) => {
                                                    const newValue = e.target.value;
                                                    setEditingDateRow(null);
                                                    if (dependencyDragRef.current) return;
                                                    if (newValue && Number(newValue) !== row.start) {
                                                        const newDay = Number(newValue);
                                                        const duration = row.end != null && row.start != null ? row.end - row.start : 0;
                                                        // FS clamp: picked start moves both dates,
                                                        // so it clamps like a move (Bill, 2026-07-29)
                                                        let start = newDay;
                                                        let end = newDay + duration;
                                                        if (row.type === 'subtask') {
                                                            const { ctx } = buildFsCtx();
                                                            const key = `${row.ref}|${row.projectId || ''}`;
                                                            if (ctx.tasksByKey[key]) {
                                                                const clamped = clampMove(ctx, key, start, end);
                                                                start = clamped.start;
                                                                end = clamped.end;
                                                            }
                                                        }
                                                        onTaskUpdate?.(row.originalId, {
                                                            relativeStart: start,
                                                            relativeEnd: end,
                                                            isSubtask: row.type === 'subtask',
                                                            projectId: row.projectId
                                                        });
                                                    }
                                                }}
                                                onChange={(e) => {
                                                    e.target.blur();
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <option value="">Select...</option>
                                                {dateDropdownOptions.map(opt => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <span className={row.start == null && !readOnly && !row.isSummary ? 'text-gray-400 italic' : ''}>
                                                {row.start != null ? formatRelativeDay(row.start) : (readOnly || row.isSummary ? '-' : 'Click to set')}
                                            </span>
                                        ))}
                                    </div>

                                    {/* Duration. The cell and its width stay below the text
                                        threshold - only the contents go, so nothing reflows. */}
                                    <div
                                        className="flex items-center justify-center px-2 text-[calc(15px*var(--gantt-scale))] text-gray-600"
                                        style={{ width: durationColWidth }}
                                    >
                                        {showText && (row.start != null && row.end != null
                                            ? calculateTemplateDuration(row.start, row.end, weekendWork[weekendWorkKey(row.projectId, row.originalId)] || {})
                                            : '-')}
                                    </div>
                                    </>)}
                                {/* No effort cell - the left-panel column was
                                    vetoed; see the header's ⛔ note. */}
                                </div>

                                {/* Timeline bar area */}
                                <div
                                    data-gantt-pan
                                    className="relative flex-shrink-0 overflow-hidden"
                                    style={{ width: timelineWidth, cursor: 'grab' }}
                                >
                                    {/* Grid is drawn ONCE for the whole chart, behind
                                        every row - see the grid layer above the rows. */}

                                    {/* Task bar */}
                                    {pos && (() => {
                                        const segments = row.type === 'subtask' && !row.isSummary
                                            ? calculateTemplateBarSegments(row, pixelsPerDay, dateRange.minDay, weekendWork)
                                            : [];
                                        const hasWeekendGaps = segments.some(s => s.isWeekendGap);

                                        const blockers = row.type === 'subtask' && row.originalId
                                            ? getBlockers(blockedTasksMap, row.originalId, row.projectId, true)
                                            : [];
                                        const isBlocked = blockers.length > 0;
                                        const hasIssueBlocker = blockers.some(b => b.type === 'issue');

                                        // ⛔ ONE LABEL PER BAR, AND EVERYTHING DOWNSTREAM USES THIS
                                        // SAME STRING (T399) - bar text, word flow, overflow check
                                        // and after-bar label. Two of the four disagreed until
                                        // 2026-08-30, so a meeting renamed itself depending on
                                        // whether a weekend fell inside its dates.
                                        // T471 - the effort suffix, `(Est.4d)`.
                                        //
                                        // ⛔ APPENDED WHERE barLabel IS DEFINED, for the same
                                        // reason as in GanttChart: the word flow, the overflow
                                        // measurement, the bar text and the after-bar label all
                                        // read this ONE string, and a suffix added at a render
                                        // site would leave every measured width short.
                                        //
                                        // ESTIMATE ONLY (D5) - a template is never worked on, so
                                        // there is no actual, no overrun and nothing to colour.
                                        // That is why this stays a plain string where GanttChart
                                        // needs a split render.
                                        //
                                        // ⛔ AND NOT THE CLIENT'S LANE (D1, 2026-09-02) - a
                                        // template has a `Team` lane too, and its tasks seed every
                                        // project built from it. Two arguments: this component has
                                        // no `project` in scope either.
                                        const effortSuffix = showEffort && row.type === 'task'
                                            && !isClientLaneTask(row, teamSwimlaneId)
                                            ? effortLabel(row.effortEstimated, null, { estimateOnly: true })
                                            : null;
                                        const baseLabel = row.isMeeting ? `${row.text} (Meeting)` : row.text;
                                        const barLabel = effortSuffix ? `${baseLabel} ${effortSuffix}` : baseLabel;

                                        // ⚠️ HOISTED OUT OF THE SEGMENT MAP (T391): both ran once per
                                        // segment, and the after-bar label needs the flows once,
                                        // before the map. Same inputs, same outputs, one call.
                                        const workingSegs = segments.filter(s => !s.isWeekendGap);
                                        const segmentFlows = hasWeekendGaps
                                            ? flowWords(barLabel, workingSegs.map(w => w.width), nameMeasure)
                                            : [];

                                        // The percentage decides FIRST, then the name.
                                        const percentWidth = row.progress > 0 ? percentMeasure(`${row.progress}%`) : 0;
                                        const percentShown = row.progress !== undefined && row.progress > 0
                                            && !hasWeekendGaps && showText
                                            && percentLabelFits(
                                                Math.max(pos.width, 20),
                                                percentWidth,
                                                hasIssueBlocker ? BLOCKED_ICON_WIDTH : 0
                                            );

                                        // ⛔ RESERVE WHAT ACTUALLY RENDERS, NOT WHAT MIGHT.
                                        const nameReserved = (percentShown ? percentWidth + 10 : 0)
                                            + (hasIssueBlocker && showText ? BLOCKED_ICON_WIDTH : 0);

                                        // A weekend-gapped bar is NOT measured: flowWords already
                                        // decides, setting truncate on the piece it could not fit.
                                        const nameOverflows = hasWeekendGaps
                                            ? segmentFlows.some(f => f.truncate)
                                            : barNameOverflows(
                                                Math.max(pos.width, 20),
                                                barNameMeasure(barLabel),
                                                nameReserved
                                            );

                                        return (
                                        <>
                                        <div
                                            className={`absolute rounded flex items-center z-10 ${
                                                isDragging ? 'opacity-70 ring-2 ring-primary-500' : ''
                                            } ${
                                                pos.isPushed ? 'opacity-80 ring-2 ring-orange-400' : ''
                                            } ${
                                                dependencyDrag && row.type === 'subtask' && row.id !== dependencyDrag.sourceRowId
                                                    ? 'ring-2 ring-blue-400 ring-opacity-50' : ''
                                            } ${
                                                // Every bar is h-7 (Bill, 2026-08-23) - see the same
                                                // line in GanttChart.js. Summary rows were h-5, which
                                                // left main-task height inconsistent with itself.
                                                'h-[calc(1.75rem*var(--gantt-scale))]'
                                            }`}
                                            style={{
                                                // Dependency-chain dimming, matching the 0.25 the
                                                // lines use. Bars only - row backgrounds and the
                                                // left panel stay legible. undefined when no chain
                                                // is hovered, so the drag/pushed opacity classes
                                                // above are never overridden.
                                                opacity: chainRowIds && !chainRowIds.has(row.id)
                                                    ? 0.25
                                                    : undefined,
                                                left: pos.left,
                                                width: pos.width,
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                background: hasWeekendGaps ? 'transparent' : getBarGradient(barColour),
                                                // Segmented bars are transparent here - their segments carry the
                                                // shadow, or it would draw across the weekend gaps.
                                                // Was '2px 3px 6px rgba(0,0,0,0.4)' - a wide soft smudge. Same geometry as
                                                // organise-app's T163 button shadow (0 1px 2px), a step darker: gray-600 at
                                                // 0.75. Inline because it always was; a class loses to an inline style.
                                                ...(hasWeekendGaps ? {} : { boxShadow: '0 1px 2px rgba(75, 85, 99, 0.75)' }),
                                                minWidth: 20,
                                                cursor: readOnly ? 'default' :
                                                    (row.type === 'status' || row.type === 'epic') ? 'default' :
                                                    (dependencyDrag && row.type === 'subtask' ? 'crosshair' : 'grab'),
                                                ...(isNeedsInfo(row.status) && !row.isClosed ? { animation: 'needsInfoPulse 2s ease-in-out infinite' } : {})
                                            }}
                                            onPointerDown={(e) => {
                                                // Ctrl+click on subtask
                                                if (e.ctrlKey && row.type === 'subtask' && !readOnly) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    const rect = e.currentTarget.getBoundingClientRect();

                                                    // Check for weekend click on day scale
                                                    if (currentScale === 'day' && onWeekendWorkChange) {
                                                        const clickedDay = getClickedDay(
                                                            e.clientX,
                                                            rect.left,
                                                            rect.width,
                                                            row.start,
                                                            row.end
                                                        );

                                                        if (clickedDay != null && isTemplateWeekend(clickedDay)) {
                                                            setWeekendPopup({
                                                                row,
                                                                weekendDay: clickedDay,
                                                                x: e.clientX,
                                                                y: rect.bottom + 5
                                                            });
                                                            return;
                                                        }
                                                    }

                                                    // Otherwise show status popup
                                                    if (onStatusChange) {
                                                        const taskBlockers = getBlockers(blockedTasksMap, row.originalId, row.projectId, true);
                                                        setStatusPopup({
                                                            row,
                                                            x: rect.left,
                                                            y: rect.bottom + 5,
                                                            blockers: taskBlockers
                                                        });
                                                    }
                                                    return;
                                                }
                                                if (e.shiftKey && row.type === 'subtask') {
                                                    handleDependencyDragStart(e, row);
                                                } else if (!readOnly && row.type !== 'status') {
                                                    handleDragStart(e, row);
                                                }
                                            }}
                                            onMouseUp={(e) => {
                                                if (dependencyDrag) handleDependencyDrop(e, row);
                                            }}
                                            onMouseEnter={(e) => handleBarMouseEnter(e, row)}
                                            onMouseLeave={handleBarMouseLeave}
                                            onContextMenu={(e) => {
                                                if (row.type === 'subtask' && !readOnly && onAssigneeChange) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setAssigneePopup({
                                                        row,
                                                        x: e.clientX,
                                                        y: e.clientY + 5
                                                    });
                                                }
                                            }}
                                        >
                                            {/* Render segments for subtasks with weekend gaps */}
                                            {hasWeekendGaps && segments.map((seg, segIdx) => {
                                                return seg.isWeekendGap ? (
                                                    <svg
                                                        key={segIdx}
                                                        className="absolute pointer-events-none"
                                                        style={{
                                                            left: seg.left - pos.left,
                                                            width: seg.width,
                                                            top: '50%',
                                                            transform: 'translateY(-50%)',
                                                            height: '12px',
                                                            overflow: 'visible'
                                                        }}
                                                        viewBox={`0 0 ${seg.width} 12`}
                                                        preserveAspectRatio="none"
                                                    >
                                                        <line
                                                            x1="0"
                                                            y1="6"
                                                            x2={seg.width - 6}
                                                            y2="6"
                                                            stroke="#9ca3af"
                                                            strokeWidth="2"
                                                            strokeDasharray="4 3"
                                                        />
                                                        <polygon
                                                            points={`${seg.width - 9},1.5 ${seg.width},6 ${seg.width - 9},10.5`}
                                                            fill="#9ca3af"
                                                        />
                                                    </svg>
                                                ) : (
                                                    (() => {
                                                        // (workingSegs and the flow are hoisted above
                                                        // the map - T391)
                                                        const totalWorkingWidth = workingSegs.reduce((sum, s) => sum + s.width, 0);
                                                        const segWorkingIndex = workingSegs.indexOf(seg);
                                                        const cumulativeWidth = workingSegs.slice(0, segWorkingIndex).reduce((sum, s) => sum + s.width, 0);
                                                        const flow = segmentFlows[segWorkingIndex]
                                                            || { text: '', startChevron: false, endChevron: false, truncate: false };
                                                        const progressWidth = (row.progress / 100) * totalWorkingWidth;

                                                        let segProgressWidth = 0;
                                                        if (progressWidth > cumulativeWidth) {
                                                            segProgressWidth = Math.min(progressWidth - cumulativeWidth, seg.width);
                                                        }
                                                        const segProgressPercent = seg.width > 0 ? (segProgressWidth / seg.width) * 100 : 0;

                                                        return (
                                                            <div
                                                                key={segIdx}
                                                                className="absolute pointer-events-none rounded flex items-center overflow-hidden"
                                                                style={{
                                                                    left: seg.left - pos.left,
                                                                    width: seg.width,
                                                                    top: 0,
                                                                    bottom: 0,
                                                                    background: getBarGradient(barColour),
                                                                    boxShadow: '0 1px 2px rgba(75, 85, 99, 0.75)',
                                                                    ...(isNeedsInfo(row.status) && !row.isClosed ? { animation: 'needsInfoPulse 2s ease-in-out infinite' } : {})
                                                                }}
                                                            >
                                                                {segProgressPercent > 0 && (
                                                                    <div
                                                                        className="absolute left-0 top-0 bottom-0 rounded-l pointer-events-none"
                                                                        style={{
                                                                            width: `${segProgressPercent}%`,
                                                                            backgroundColor: 'rgba(0,0,0,0.15)'
                                                                        }}
                                                                    />
                                                                )}
                                                                {/* Whole-word name flow with › continuation chevrons - see
                                                                    gantt/nameFlow.js and GANTT-NAME-FLOW-2026-08-21.md */}
                                                                {/* Double glow, ported from organise-app 2026-08-23. Its L058
                                                                    warns that a shadow tuned for one palette is not tuned for
                                                                    another - this is the reverse port of the value that lesson
                                                                    rejected, taken deliberately (Bill, 2026-08-23). Revert this
                                                                    part alone if it smudges. */}
                                                                {flow.startChevron && showText && (
                                                                    <span className="relative z-10 flex-shrink-0 text-lg leading-none font-bold text-white pl-1 pointer-events-none"
                                                                          style={{ textShadow: '0 0 2px rgba(0, 0, 0, 0.9), 0 0 4px rgba(0, 0, 0, 0.75), 0 0 6px rgba(0, 0, 0, 0.6)' }}>
                                                                        ›
                                                                    </span>
                                                                )}
                                                                {flow.text && showText && (
                                                                    <span className={`relative z-10 min-w-0 text-[calc(18px*var(--gantt-scale))] font-semibold text-white pointer-events-none whitespace-nowrap ${flow.startChevron ? 'pl-1' : 'pl-3'} ${(flow.truncate || segWorkingIndex === workingSegs.length - 1) ? 'truncate' : ''} ${row.isClosed ? 'line-through opacity-75' : ''}`}
                                                                          style={{ textShadow: '0 0 2px rgba(0, 0, 0, 0.9), 0 0 4px rgba(0, 0, 0, 0.75), 0 0 6px rgba(0, 0, 0, 0.6)' }}>
                                                                        {flow.text}
                                                                    </span>
                                                                )}
                                                                {flow.endChevron && showText && (
                                                                    <span className="relative z-10 flex-shrink-0 text-lg leading-none font-bold text-white pointer-events-none ml-auto pl-1 pr-1"
                                                                          style={{ textShadow: '0 0 2px rgba(0, 0, 0, 0.9), 0 0 4px rgba(0, 0, 0, 0.75), 0 0 6px rgba(0, 0, 0, 0.6)' }}>
                                                                        ›
                                                                    </span>
                                                                )}
                                                                {/* ⛔ The box here is the SEGMENT, not the bar - and the blocked
                                                                    icon is NOT reserved, because segments are absolute while
                                                                    the icon sits in the bar's own flex flow. */}
                                                                {segWorkingIndex === workingSegs.length - 1 && row.progress > 0 && showText
                                                                    && percentLabelFits(seg.width, percentMeasure(`${row.progress}%`), 0) && (
                                                                    <span
                                                                        className="relative z-10 text-[calc(18px*var(--gantt-scale))] text-white/90 pointer-events-none whitespace-nowrap pr-2.5 ml-auto"
                                                                        style={{ textShadow: '0 0 2px rgba(0, 0, 0, 0.9), 0 0 4px rgba(0, 0, 0, 0.75), 0 0 6px rgba(0, 0, 0, 0.6)' }}
                                                                    >
                                                                        {row.progress}%
                                                                    </span>
                                                                )}
                                                            </div>
                                                        );
                                                    })()
                                                );
                                            })}

                                            {/* Resize handles. The POSITIONING moves to the tooltip
                                                wrapper, as an inline style rather than classes - see
                                                Tooltip's `style` note. */}
                                            {!readOnly && !row.isSummary && !dependencyDrag && (
                                                <Tooltip
                                                    text="Drag to change start"
                                                    fixed
                                                    position="top"
                                                    style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 8 }}
                                                >
                                                    <div className="w-full h-full bg-white/40 hover:bg-white/70 cursor-ew-resize rounded-l" />
                                                </Tooltip>
                                            )}
                                            {!readOnly && !row.isSummary && !dependencyDrag && (
                                                <Tooltip
                                                    text="Drag to change end"
                                                    fixed
                                                    position="top"
                                                    style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 8 }}
                                                >
                                                    <div className="w-full h-full bg-white/40 hover:bg-white/70 cursor-ew-resize rounded-r" />
                                                </Tooltip>
                                            )}

                                            {/* Blocked indicator */}
                                            {isBlocked && (
                                                <div
                                                    className="absolute left-0 top-0 bottom-0 rounded-l pointer-events-none z-20"
                                                    style={{ backgroundColor: '#dc2626', width: '3px' }}
                                                />
                                            )}

                                            {/* Progress fill - non-segmented bars */}
                                            {row.progress > 0 && !hasWeekendGaps && (
                                                <div
                                                    className="absolute left-0 top-0 bottom-0 rounded-l pointer-events-none"
                                                    style={{
                                                        width: `${row.progress}%`,
                                                        backgroundColor: 'rgba(0,0,0,0.15)'
                                                    }}
                                                />
                                            )}

                                            {/* Weekend markers within bar */}
                                            {row.type === 'subtask' && row.start != null && row.end != null && currentScale === 'day' && (() => {
                                                const totalDays = Math.max(row.end - row.start + 1, 1);
                                                const markers = [];
                                                for (let d = row.start + 1; d <= row.end; d++) {
                                                    if (isTemplateWeekend(d)) {
                                                        const dayOffset = d - row.start;
                                                        const leftPercent = (dayOffset / totalDays) * 100;
                                                        markers.push(
                                                            <div
                                                                key={d}
                                                                className="absolute top-0 bottom-0 pointer-events-none"
                                                                style={{
                                                                    left: `${leftPercent}%`,
                                                                    width: '1px',
                                                                    backgroundColor: 'rgba(255,255,255,0.2)'
                                                                }}
                                                            />
                                                        );
                                                    }
                                                }
                                                return markers;
                                            })()}

                                            {/* Blocked indicator icon. Hidden below the text
                                                threshold (D11) - a fixed 12px icon in an 8px bar
                                                overflows it. The 3px red strip is inset-y, so
                                                blocking is still signalled at every reading size. */}
                                            {hasIssueBlocker && showText && (
                                                <Tooltip
                                                    fixed
                                                    position="top"
                                                    className="ml-1 flex-shrink-0 z-10"
                                                    text={`Blocked by: ${blockers.filter(b => b.type === 'issue').map(b => `Issue ${b.id}: ${b.title}`).join(', ')}`}
                                                >
                                                    <FaExclamationTriangle className="text-white" size={12} />
                                                </Tooltip>
                                            )}
                                            {/* Text on bar */}
                                            {!hasWeekendGaps && showText && (
                                                <span
                                                    className={`relative z-10 pl-3 pr-1 truncate pointer-events-none flex-1 min-w-0 text-[calc(18px*var(--gantt-scale))] font-semibold ${row.isMeeting ? 'text-gray-700' : 'text-white'} ${row.isClosed ? 'line-through opacity-75' : ''}`}
                                                    style={row.isMeeting ? undefined : { textShadow: '0 0 2px rgba(0, 0, 0, 0.9), 0 0 4px rgba(0, 0, 0, 0.75), 0 0 6px rgba(0, 0, 0, 0.6)' }}
                                                >
                                                    {barLabel}
                                                </span>
                                            )}
                                            {/* Progress percentage. The fit was decided above, before
                                                the name's own check - it does not depend on the name,
                                                while the name's check depends on it. */}
                                            {percentShown && (
                                                <span
                                                    className="z-10 text-[calc(18px*var(--gantt-scale))] text-white/90 pointer-events-none whitespace-nowrap pr-2.5 flex-shrink-0"
                                                    style={{ textShadow: '0 0 2px rgba(0, 0, 0, 0.9), 0 0 4px rgba(0, 0, 0, 0.75), 0 0 6px rgba(0, 0, 0, 0.6)' }}
                                                >
                                                    {row.progress}%
                                                </span>
                                            )}
                                        </div>

                                        {/* The full name, in italics, OUTSIDE the bar and to its
                                            right - and ONLY when the name inside had to be cut
                                            (Bill, 2026-08-30). Style ported verbatim from
                                            organise-app's GanttRow.js.

                                            pointer-events-none and z-[5] are behavioural, not
                                            cosmetic: never eat a drag, never paint over the next
                                            bar. The 16px offset clears the 8px resize grip, which
                                            sits on the bar's own right edge. The row's timeline cell
                                            is overflow-hidden, so a name running past the chart's
                                            right edge is clipped there and adds no scroll width.

                                            ⛔ .bar-strike, never line-through: the latter sits below
                                            centre at this size. (This file's own bar spans are still
                                            on line-through - converting them is a separate
                                            decision, recorded in the plan's §8.) */}
                                        {nameOverflows && showText && (
                                            <span
                                                className={`pointer-events-none absolute z-[5] ${AFTER_BAR_NAME_CLASSES}`}
                                                style={{
                                                    left: pos.left + pos.width + AFTER_BAR_NAME_OFFSET,
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    textShadow: AFTER_BAR_NAME_GLOW
                                                }}
                                            >
                                                {row.isClosed
                                                    ? <span className="bar-strike bar-strike-thin">{barLabel}</span>
                                                    : barLabel}
                                            </span>
                                        )}
                                        </>
                                        );
                                    })()}
                                </div>
                            </div>
                        );
                    })}

                    {/* Dependency drag line */}
                    {dependencyDrag && (
                        <svg
                            className="absolute inset-0 pointer-events-none z-30"
                            style={{ width: leftPanelWidth + timelineWidth, height: rows.length * rowHeight + 80 }}
                        >
                            <line
                                x1={dependencyDrag.startX}
                                y1={dependencyDrag.startY}
                                x2={cursorPos.x}
                                y2={cursorPos.y}
                                stroke="#3b82f6"
                                strokeWidth="2"
                                strokeDasharray="5,5"
                            />
                            <circle
                                cx={cursorPos.x}
                                cy={cursorPos.y}
                                r="6"
                                fill="#3b82f6"
                            />
                        </svg>
                    )}

                    {/* Dependency arrows */}
                    <DependencyArrows
                        effectiveLinks={effectiveLinks}
                        rows={rows}
                        readOnly={readOnly}
                        headerHeight={headerHeight}
                        currentScale={currentScale}
                        getDisplayPosition={getBarPosition}
                        getDateGap={(sourceRow, targetRow) => {
                            return (targetRow.start != null && sourceRow.end != null)
                                ? targetRow.start - sourceRow.end
                                : 0;
                        }}
                        onDependencyDelete={onDependencyDelete}
                        onDependencyColorChange={onDependencyColorChange}
                        setColorPopup={setColorPopup}
                        setTopLinkKey={setTopLinkKey}
                        setPendingLinks={setPendingLinks}
                        leftPanelWidth={leftPanelWidth}
                        timelineWidth={timelineWidth}
                        rowHeight={rowHeight}
                        zoomLevel={zoomLevel}
                        onLinkHover={handleLinkHover}
                    />

                    {/* Bottom border line */}
                    <div
                        className="absolute border-b border-gray-300"
                        style={{
                            left: 0,
                            right: 0,
                            top: (headerHeight || (currentScale === 'day' ? 64 : 36)) + (rows.length * rowHeight)
                        }}
                    />
                </div>
            </div>

            {/* Footer with time slider */}
            <div className="flex items-center gap-4 px-4 py-2 border-t border-gray-300 bg-gray-50 flex-shrink-0">
                <span className="text-[16px] text-gray-600 whitespace-nowrap">
                    Week 1
                </span>
                <div className="flex-1 flex items-center gap-2">
                    <DateSlider
                        value={scrollPercent}
                        onScrub={handleSliderScrub}
                        onCommit={handleSliderCommit}
                        step={sliderStep}
                    />
                </div>
                <span className="text-[16px] text-gray-600 whitespace-nowrap">
                    Week {dateRange.maxDay ? Math.ceil(dateRange.maxDay / 7) : 1}
                </span>
            </div>

            {/* Status change popup */}
            <StatusChangePopup
                statusPopup={statusPopup}
                taskStatuses={taskStatuses}
                onStatusChange={onStatusChange}
                onClosureRequired={onClosureRequired}
                onClose={() => setStatusPopup(null)}
            />

            {/* Assignee change popup */}
            <AssigneeChangePopup
                assigneePopup={assigneePopup}
                members={members}
                onAssigneeChange={onAssigneeChange}
                onClose={() => setAssigneePopup(null)}
            />

            {/* Weekend work popup - template version with relative labels */}
            {weekendPopup && (
                <div
                    className="fixed inset-0 z-50"
                    onClick={() => setWeekendPopup(null)}
                >
                    <div
                        className="absolute bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[150px]"
                        style={{ left: weekendPopup.x, top: weekendPopup.y }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {(() => {
                            const subtaskId = String(weekendPopup.row.originalId);
                            const rowProjectId = weekendPopup.row.projectId;
                            const clickedDay = weekendPopup.weekendDay;
                            // Get both Saturday and Sunday for this weekend
                            const saturday = clickedDay % 7 === 6
                                ? clickedDay
                                : clickedDay - 1;
                            const sunday = clickedDay % 7 === 0
                                ? clickedDay
                                : clickedDay + 1;
                            const satKey = String(saturday);
                            const sunKey = String(sunday);
                            const satWorked = weekendWork[weekendWorkKey(rowProjectId, subtaskId)]?.[satKey] === true;
                            const sunWorked = weekendWork[weekendWorkKey(rowProjectId, subtaskId)]?.[sunKey] === true;

                            return (
                                <>
                                    <div className="px-3 py-1.5 text-xs font-medium text-gray-500 border-b border-gray-100">
                                        Weekend: {formatRelativeDay(saturday)} - {formatRelativeDay(sunday)}
                                    </div>
                                    {/* Saturday */}
                                    <button
                                        className={`w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 flex items-center justify-between ${
                                            satWorked ? 'bg-blue-50' : ''
                                        }`}
                                        onClick={() => {
                                            onWeekendWorkChange(subtaskId, satKey, !satWorked, rowProjectId);
                                            setWeekendPopup(null);
                                        }}
                                    >
                                        <span>Saturday ({formatRelativeDay(saturday)})</span>
                                        <span className={`text-xs px-1.5 py-0.5 rounded ${satWorked ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
                                            {satWorked ? 'Worked' : 'Not Worked'}
                                        </span>
                                    </button>
                                    {/* Sunday */}
                                    <button
                                        className={`w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 flex items-center justify-between ${
                                            sunWorked ? 'bg-blue-50' : ''
                                        }`}
                                        onClick={() => {
                                            onWeekendWorkChange(subtaskId, sunKey, !sunWorked, rowProjectId);
                                            setWeekendPopup(null);
                                        }}
                                    >
                                        <span>Sunday ({formatRelativeDay(sunday)})</span>
                                        <span className={`text-xs px-1.5 py-0.5 rounded ${sunWorked ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
                                            {sunWorked ? 'Worked' : 'Not Worked'}
                                        </span>
                                    </button>
                                    {/* Both days */}
                                    <div className="border-t border-gray-100 mt-1 pt-1">
                                        <button
                                            className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 text-gray-700"
                                            onClick={() => {
                                                setWeekendPopup(null);
                                                onWeekendWorkChange(subtaskId, [
                                                    { dateKey: satKey, worked: true },
                                                    { dateKey: sunKey, worked: true }
                                                ], null, rowProjectId);
                                            }}
                                        >
                                            Mark both worked
                                        </button>
                                        <button
                                            className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 text-gray-700"
                                            onClick={() => {
                                                setWeekendPopup(null);
                                                onWeekendWorkChange(subtaskId, [
                                                    { dateKey: satKey, worked: false },
                                                    { dateKey: sunKey, worked: false }
                                                ], null, rowProjectId);
                                            }}
                                        >
                                            Mark both not worked
                                        </button>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </div>
            )}

            {/* Dependency colour picker popup */}
            <DependencyColourPicker
                colorPopup={colorPopup}
                onDependencyColorChange={onDependencyColorChange}
                setTopLinkKey={setTopLinkKey}
                onClose={() => setColorPopup(null)}
            />

            {/* Custom tooltip */}
            {linkTip && (
                <div
                    className="px-3 py-2 text-sm bg-gray-800 text-white rounded-lg max-w-xs"
                    style={{
                        position: 'fixed',
                        left: linkTip.x + 18,
                        top: linkTip.y + 18,
                        zIndex: 9999,
                        pointerEvents: 'none',
                        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3)'
                    }}
                >
                    {linkTip.content}
                </div>
            )}
            {/* The question a finger's bar drag asks (T404). It draws its own
                backdrop. formatRelativeDay, not a date: a template has no
                calendar, and W1D3 is what every other surface in this editor
                shows. */}
            <GanttDragConfirm
                confirm={dragConfirm}
                formatDate={formatRelativeDay}
                countLabel={dragConfirmCountLabel}
                onCancel={() => {
                    setDragConfirm(null);
                    clearDragPreviews();
                }}
                onConfirm={() => {
                    applyDragCommit(dragConfirm);
                    setDragConfirm(null);
                }}
            />

            <CustomTooltip
                tooltip={tooltip}
                tooltipPos={tooltipPos}
                tooltipRef={tooltipRef}
                tooltipTimeoutRef={tooltipTimeoutRef}
                setTooltip={setTooltip}
                setTooltipPos={clearTooltipPos}
            />
        </div>
    );
};

export default TemplateGantt;
